import React, { useContext, useEffect, useRef, useState, useMemo, useLayoutEffect, useCallback } from "react";
import cx from "classnames";
import PropTypes from "prop-types";

import global from "../../../scss/dhp.scss";

import { Icon } from "../../ui/icon";
import {
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import {
  changeWidgetColorAccordingBackground,
  colorHexCodeValidation,
  hexToRGB,
  getChartCanvasPNGSrc,
} from "../../../_helpers/utils";

import { CHART } from "../../../constants/editor";
import { EXCEL_COLUMN_MAP, EXCEL_COLUMN_REVERSE_MAP } from "../../../constants/chart";
import { widgetConfig } from "../editor_config";
import useAlignment from "../../../hooks/useAlignment";
import useAddWidget from "../../../hooks/useAddWidget";
import useElementInnerHtml from "../../../hooks/useElementInnerHtml";
import { EditorContext } from "../../../containers/editor/EditorLayout";
import ColorPicker from "../../ui/colorPicker";
import FontFamily from "../ContextualMenu/FontFamily";
import FontSize from "../ContextualMenu/FontSize";
import useClickOutsideExceptContextMenu from "../../../hooks/useClickOutsideExceptContextMenu";
import { Chart as ChartJS, Tooltip } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Input } from "../../ui/input";

let style = Object.assign({}, global);

ChartJS.register(ChartDataLabels);

const fontSizeList = widgetConfig?.tables?.fontSize;

const ColorPickerWidget = ({ color, setColor, moveDropdownUp }) => {
  const [widgetColor, setWidgetColor] = useState();
  const [eyedropperColor, setEyedropperColor] = useState();
  const targetModalContainer = document.getElementById("editorBody");
  //update color if user chose color from eyedropper
  useEffect(() => {
    if (eyedropperColor) {
      setColor(eyedropperColor);
    }
  }, [eyedropperColor]);

  useEffect(() => {
    let isHex = colorHexCodeValidation(color);
    if (isHex) {
      let rgbColor = hexToRGB(color);
      setWidgetColor(changeWidgetColorAccordingBackground(rgbColor));
    }
  }, [color]);

  return (
    <>
      {widgetColor && (
        <UncontrolledDropdown
          setActiveFromChild
          className={cx(style["color-section-block"], style["color-picker-dropdown"])}>
          <DropdownToggle tag="a" className={cx(style["p-0"], style["d-flex"])}>
            <Icon
              icon="ui-fill-color2"
              style={{
                backgroundColor: color,
                color: widgetColor,
                padding: "0.5rem",
                borderRadius: "0.375rem",
              }}
            />
          </DropdownToggle>
          <DropdownMenu
            container={targetModalContainer}
            style={{ zIndex: 9999 }}
            className={cx(style["border-0"], style["p-0"])}
            modifiers={[
              {
                name: "computeStyles",
                options: {
                  adaptive: false,
                },
              },
              ...(moveDropdownUp
                ? [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -moveDropdownUp],
                      },
                    },
                  ]
                : []),
            ]}>
            <ColorPicker
              color={color}
              setColor={setColor}
              isHideTransparentOpt={true}
              eyedropperColor={eyedropperColor}
              setEyedropperColor={setEyedropperColor}
            />
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
    </>
  );
};

ColorPickerWidget.propTypes = {
  color: PropTypes.string.isRequired,
  setColor: PropTypes.func.isRequired,
};

const ColumnColorPicker = React.memo(
  ({ colCode, setColumnColorClicked, color, setColor, setEyeDropperColor, eyeDropperColor, isOpen }) => {
    const targetModalContainer = document.getElementById("editorBody");
    return (
      <UncontrolledDropdown
        onClick={e => e.stopPropagation()}
        className={cx(
          style["color-section-block"],
          style["color-picker-dropdown"],
          style["d-flex"],
          style["justify-content-center"]
        )}>
        <DropdownToggle tag="a" className={cx(style["p-0"], style["d-flex"])}>
          <div
            onClick={() => {
              setColumnColorClicked(colCode);
            }}
            style={{ backgroundColor: color, height: "10px", width: "10px" }}></div>
        </DropdownToggle>

        {isOpen && (
          <DropdownMenu
            container={targetModalContainer}
            style={{ zIndex: 9999 }}
            className={cx(style["border-0"], style["p-0"], style["map-color-dropdown"])}>
            <ColorPicker
              color={color}
              setColor={setColor}
              eyedropperColor={eyeDropperColor}
              setEyedropperColor={setEyeDropperColor}
              // setUpdateContext={setUpadateData}
            />
          </DropdownMenu>
        )}
      </UncontrolledDropdown>
    );
  }
);

ColumnColorPicker.displayName = "ColumnColorPicker";

ColumnColorPicker.propTypes = {
  color: PropTypes.string,
  setSelectedAreaColor: PropTypes.func,
  eyedropperColor: PropTypes.string,
  setEyedropperColor: PropTypes.func,
};

const getChartConfig = (chartType, data, chartConfig, labelFormat) => {
  const isBarChart = ["vertical_bar", "stacked_vertical_bar", "horizontal_bar", "stacked_horizontal_bar"].includes(
    chartType
  );
  const isStacked = ["stacked_vertical_bar", "stacked_horizontal_bar"].includes(chartType);
  const isHorizontal = ["horizontal_bar", "stacked_horizontal_bar"].includes(chartType);
  const isPieOrDonut = ["pie", "donut"].includes(chartType);
  const isLineArea = ["area", "line"].includes(chartType);

  const chartTypeMap = {
    pie: "pie",
    donut: "doughnut",
    area: "line",
    default: isBarChart ? "bar" : chartType,
  };
  const type = chartTypeMap[chartType] || chartTypeMap.default;

  const calculateYBounds = (datasets, labels, isStacked, isHorizontal) => {
    let maxValue = -Infinity;
    let minValue = Infinity;

    if (isStacked) {
      const posSums = Array(labels.length).fill(0);
      const negSums = Array(labels.length).fill(0);

      datasets.forEach(dataset => {
        dataset.data.forEach((value, index) => {
          if (typeof value === "number" && !isNaN(value)) {
            if (value >= 0) posSums[index] += value;
            else negSums[index] += value;
          }
        });
      });

      maxValue = Math.max(...posSums);
      minValue = Math.min(...negSums);
    } else {
      const allValues = datasets.flatMap(dataset =>
        dataset.data.filter(value => typeof value === "number" && !isNaN(value))
      );

      maxValue = Math.max(...allValues);
      minValue = Math.min(...allValues);
    }

    if (maxValue === -Infinity) maxValue = 0;
    if (minValue === Infinity) minValue = 0;

    const getNiceStep = value => {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      if (normalized <= 2) return 0.2 * magnitude;
      if (normalized <= 5) return 0.5 * magnitude;
      return 1 * magnitude;
    };

    const getNiceMax = value => {
      const padded = value * 1.1;
      const step = getNiceStep(padded);
      return Math.ceil(padded / step) * step;
    };

    const getNiceMin = value => {
      if (value >= 0) return 0;
      const padded = value * 1.1;
      const step = getNiceStep(Math.abs(padded));
      return -Math.ceil(Math.abs(padded) / step) * step;
    };

    let min = minValue >= 0 ? 0 : getNiceMin(minValue);
    let max = maxValue <= 0 ? 0 : getNiceMax(maxValue);

    return { min, max };
  };

  const { min, max } = isPieOrDonut
    ? { min: undefined, max: undefined }
    : calculateYBounds(data.datasets, data.labels, isStacked, isHorizontal);

  const cleanData = data.datasets.map(dataset => ({
    ...dataset,
    data: dataset.data.map(value => (value == "" ? null : value)),
  }));

  const adjustedData = isPieOrDonut ? chartConfig.pieData : { datasets: cleanData, labels: data.labels };

  const adjustedDatasets = adjustedData.datasets.map(dataset => ({
    ...dataset,
    fill: chartType === "area" ? true : dataset.fill,
    backgroundColor: dataset.backgroundColor || "#4472C4",
    borderColor: dataset.borderColor || dataset.backgroundColor,
    borderWidth: dataset.borderWidth || 2,
  }));

  let baseScales;
  if (!isPieOrDonut) {
    baseScales = isHorizontal
      ? {
          x: {
            ...chartConfig.options.scales.x,
            stacked: isStacked,
            min: min,
            max: max,
            beginAtZero: false,
            ticks: {
              ...chartConfig.options.scales.x?.ticks,
              stepSize: Math.max(1, Math.round((max - min) / 5)),
            },
          },
          y: {
            ...chartConfig.options.scales.y,
            stacked: isStacked,
            ticks: {
              ...chartConfig.options.scales.y?.ticks,
              callback: function (value, index) {
                return adjustedData.labels?.[index] ?? value;
              },
            },
          },
        }
      : {
          x: {
            ...chartConfig.options.scales.x,
            stacked: isStacked,
            ticks: {
              ...chartConfig.options.scales.x?.ticks,
              callback: function (value, index) {
                return adjustedData.labels?.[index] ?? value;
              },
            },
          },
          y: {
            ...chartConfig.options.scales.y,
            stacked: isStacked,
            min: min,
            max: max,
            beginAtZero: false,
            ticks: {
              ...chartConfig.options.scales.y?.ticks,
              stepSize: Math.max(1, Math.round((max - min) / 5)),
            },
          },
        };
  }

  const basesPlugins = {
    ...chartConfig.options.plugins,
    datalabels: {
      ...chartConfig.options.plugins.datalabels,
    },

    legend: {
      ...chartConfig.options.plugins.legend,
    },
  };

  basesPlugins.datalabels.formatter = (value, context) => {
  if (isPieOrDonut && labelFormat?.name === "Percentage") {
    const data = context.dataset.data;
    const validData = data.filter(num => typeof num === 'number' && !isNaN(num));
    const total = validData.reduce((acc, num) => acc + num, 0);
    if (!total || typeof value !== 'number' || isNaN(value)) return "";
    return `${((value / total) * 100).toFixed(2)}%`;
  }
  return value;
};

  return {
    type,
    data: {
      labels: adjustedData.labels,
      datasets: adjustedDatasets,
    },
    options: {
      animation: chartConfig.options.animation,
      responsive: chartConfig.options.responsive,
      maintainAspectRatio: chartConfig.options.maintainAspectRatio,
      devicePixelRatio: chartConfig.options.devicePixelRatio,
      indexAxis: isHorizontal ? "y" : "x",
      elements: chartConfig.options.elements,
      scales: isPieOrDonut ? undefined : baseScales,
      plugins: basesPlugins,
      ...(chartType === "donut" && { cutout: "50%" }),
    },
  };
};

const ChartContent = ({ chartConfig, chartType, labelFormat, chartDropDownChange }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (canvasRef.current) {
      if (chartDropDownChange) return;
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      const chartBasedConfig = getChartConfig(chartType, { ...chartConfig.data }, chartConfig, labelFormat);

      chartInstance.current = new ChartJS(canvasRef.current, chartBasedConfig);

      chartInstance.current.update();
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartConfig.data, chartConfig.options, chartType, labelFormat]);

  return (
    <>
      <div className={cx(style["mapWrap"], style["pe-3"])} id="chartWrap">
        <canvas ref={canvasRef}></canvas>
      </div>
    </>
  );
};

ChartContent.propTypes = {
  chartConfig: PropTypes.object,
  chartType: PropTypes.string,
  labelFormat: PropTypes.object,
};

const Axes = ({ setChartOptionData, chartDefaultData, chartOptionData, clickedUndotask }) => {
  const isInitialMountDone = useRef(false);
  const [xAxesTitleInput, setXAxesTitleInput] = useState("");
  const [yaxesTitleInput, setYAxesTitleInput] = useState("");
  const [axesFontFamily, setAxesFontFamily] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.fontFamily);
  const [axesFontSize, setAxesFontSize] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.fontSize);
  const [axesTitleColor, setAxesTitleColor] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.color);
  const [showAxesLines, setShowAxesLines] = useState(true);
  const [axisColor, setAxisColor] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.axis?.color);

  const customClone = obj => {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    if (typeof obj === "function") {
      return obj.bind(obj);
    }

    return cloned;
  };

  const handleInputChange = (type, val) => {
    let state = customClone(chartOptionData);
    if (type === "xTitle") setXAxesTitleInput(val);
    else if (type === "yTitle") setYAxesTitleInput(val);
    else if (type === "showAxesLines") {
      setShowAxesLines(val);
      document.getElementById("axesLinesFormIdRadio").checked = val;

      state.options.scales.x.border.display = val;
      state.options.scales.y.border.display = val;
      setChartOptionData(state);
    }
  };

  const handleInputBlur = (type, val) => {
    let state = customClone(chartOptionData);

    if (type === "xTitle") state.options.scales.x.title.text = val;
    else if (type === "yTitle") state.options.scales.y.title.text = val;

    setChartOptionData(state);
  };

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.title.font.family;
    if (axesFontFamily === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.title.font.family = axesFontFamily;
    state.options.scales.y.title.font.family = axesFontFamily;
    setChartOptionData(state);
  }, [axesFontFamily]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.title.font.size;
    if (axesFontSize === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.title.font.size = axesFontSize;
    state.options.scales.y.title.font.size = axesFontSize;
    setChartOptionData(state);
  }, [axesFontSize]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.title.color;
    if (axesTitleColor === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.title.color = axesTitleColor;
    state.options.scales.y.title.color = axesTitleColor;
    setChartOptionData(state);
  }, [axesTitleColor]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.border.color;
    if (axisColor === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.border.color = axisColor;
    state.options.scales.y.border.color = axisColor;
    setChartOptionData(state);
  }, [axisColor]);

  useEffect(() => {
    setXAxesTitleInput(chartOptionData.options.scales.x.title.text);
    setYAxesTitleInput(chartOptionData.options.scales.y.title.text);
    setAxesFontFamily(chartOptionData.options.scales.x.title.font.family);
    setAxesFontSize(chartOptionData.options.scales.x.title.font.size);
    setAxesTitleColor(chartOptionData.options.scales.x.title.color);
    setShowAxesLines(chartOptionData.options.scales.x.border.display);
    document.getElementById("axesLinesFormIdRadio").checked = chartOptionData.options.scales.x.border.display;
    setAxisColor(chartOptionData.options.scales.x.border.color);
  }, [clickedUndotask]);

  useEffect(() => {
    document.getElementById("axesLinesFormIdRadio").checked = chartOptionData.options.scales.x.border.display;

    setXAxesTitleInput(chartOptionData.options.scales.x.title.text);
    setYAxesTitleInput(chartOptionData.options.scales.y.title.text);
    setAxesFontFamily(chartOptionData.options.scales.x.title.font.family);
    setAxesFontSize(chartOptionData.options.scales.x.title.font.size);
    setAxesTitleColor(chartOptionData.options.scales.x.title.color);
    setShowAxesLines(chartOptionData.options.scales.x.border.display);
    setAxisColor(chartOptionData.options.scales.x.border.color);
    isInitialMountDone.current = true;
  }, []);

  return (
    <>
      <div
        className={cx(
          style["line-controls-wrap"],
          style["justify-content-start"],
          style["left-shift"],
          style["d-flex"],
          style["flex-column"],
          style["mb-0"]
        )}>
        <div className={cx(style["mb-3"], style["d-flex"], style["align-items-center"])}>
          <div className={style["slidelabel"]}>X Axis Title</div>
          <Input
            cssModule={style}
            returnType="formGroup"
            type="text"
            placeholder="Axis Title"
            value={xAxesTitleInput}
            onChange={e => handleInputChange("xTitle", e.target.value)}
            onBlur={() => handleInputBlur("xTitle", xAxesTitleInput)}
          />
        </div>
        <div className={cx(style["mb-3"], style["d-flex"], style["align-items-center"])}>
          <div className={style["slidelabel"]}>Y Axis Title</div>
          <Input
            cssModule={style}
            returnType="formGroup"
            type="text"
            placeholder="Axis Title"
            value={yaxesTitleInput}
            onChange={e => handleInputChange("yTitle", e.target.value)}
            onBlur={() => handleInputBlur("yTitle", yaxesTitleInput)}
          />
        </div>
      </div>
      <div className={style["mb-2"]}>
        <ul className={cx(style["font-size-family"], style["with-color"])}>
          <FontFamily defaultFontSet={axesFontFamily} setFontFamily={setAxesFontFamily} sourceComponent={CHART} />
          <FontSize
            defaultFontSizeSet={axesFontSize}
            setFontSize={setAxesFontSize}
            defaultFontList={fontSizeList}
            sourceComponent={CHART}
          />
          <li className={cx(style["custom-tooltip"])}>
            <ColorPickerWidget color={axesTitleColor} setColor={setAxesTitleColor} moveDropdownUp={125} />
            <div className={cx(style["custom-tooltip-content"], style["top"])}>Text Color</div>
          </li>
        </ul>
      </div>
      <div className={cx(style["mb-3"], style["color-33"])}>
        <h6 className={style["fw-6"]}>Axes Lines</h6>
        <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
          <Input
            cssModule={style}
            returnType="noGroup"
            type="checkbox"
            className={style["form-check-input"]}
            id={`axesLinesFormIdRadio`}
            label="Show Axes Lines"
            defaultChecked={showAxesLines}
            onChange={e => handleInputChange("showAxesLines", e.target.checked)}
          />
        </div>
      </div>
      {showAxesLines && (
        <div className={cx(style["d-flex"], style["color-33"], style["align-items-center"])}>
          <h6 className={cx(style["font-15"], style["me-3"], style["mb-0"])}>Axis Color</h6>

          <div className={style["custom-tooltip"]}>
            <ColorPickerWidget color={axisColor} setColor={setAxisColor} />
            <div className={cx(style["custom-tooltip-content"], style["top"])}>Axis Color</div>
          </div>
        </div>
      )}
    </>
  );
};

Axes.propTypes = {
  setChartOptionData: PropTypes.func,
  chartDefaultData: PropTypes.object,
  chartOptionData: PropTypes.object,
  clickedUndotask: PropTypes.func,
};

const Grid = ({ setChartOptionData, chartDefaultData, chartOptionData, chartType, clickedUndotask }) => {
  const isInitialMountDone = useRef(false);
  const [showGrid, setShowGrid] = useState(true);
  const [gridColor, setGridColor] = useState(chartDefaultData?.chartConfig?.formatting?.grid?.color);
  const isBarChart = ["vertical_bar", "stacked_vertical_bar", "horizontal_bar", "stacked_horizontal_bar"].includes(
    chartType
  );
  const isLineArea = ["area", "line"].includes(chartType);
  const isVerticalStackedChart = ["vertical_bar", "stacked_vertical_bar"].includes(chartType);

  const isHorizontalStackedChart = ["horizontal_bar", "stacked_horizontal_bar"].includes(chartType);

  const customClone = obj => {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    if (typeof obj === "function") {
      return obj.bind(obj);
    }

    return cloned;
  };

  const handleInputChange = (type, val) => {
    let state = customClone(chartOptionData);
    if (type === "showGrid") {
      setShowGrid(val);
      document.getElementById("showGridFormIdRadio").checked = val;
      if (isVerticalStackedChart) {
        state.options.scales.x.grid.display = false;
        state.options.scales.y.grid.display = val;
      } else if (isHorizontalStackedChart) {
        state.options.scales.x.grid.display = val;

        state.options.scales.y.grid.display = false;
      } else if (isLineArea) {
        state.options.scales.x.grid.display = val;
        state.options.scales.y.grid.display = val;
      }

      setChartOptionData(state);
    }
  };

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    let currentYColor = chartOptionData.options.scales.y.grid.color;
    let currentXColor = chartOptionData.options.scales.x.grid.color;
    const isSame =
      (isVerticalStackedChart && gridColor === currentYColor) ||
      (isHorizontalStackedChart && gridColor === currentXColor) ||
      (isLineArea && gridColor === currentXColor && gridColor === currentYColor);

    if (isSame) return;

    let state = customClone(chartOptionData);
    if (isVerticalStackedChart) {
      state.options.scales.y.grid.color = gridColor;
    } else if (isHorizontalStackedChart) {
      state.options.scales.x.grid.color = gridColor;
    } else if (isLineArea) {
      state.options.scales.x.grid.color = gridColor;
      state.options.scales.y.grid.color = gridColor;
    }
    setChartOptionData(state);
  }, [gridColor]);

  useEffect(() => {
    const gridRadio = document.getElementById("showGridFormIdRadio");

    if (isVerticalStackedChart) {
      setShowGrid(chartOptionData.options.scales.y.grid.display);
      setGridColor(chartOptionData.options.scales.y.grid.color);
      if (gridRadio) gridRadio.checked = chartOptionData.options.scales.y.grid.display;
    } else if (isHorizontalStackedChart) {
      setShowGrid(chartOptionData.options.scales.x.grid.display);
      setGridColor(chartOptionData.options.scales.x.grid.color);
      if (gridRadio) gridRadio.checked = chartOptionData.options.scales.x.grid.display;
    } else if (isLineArea) {
      setShowGrid(chartOptionData.options.scales.y.grid.display);
      setGridColor(chartOptionData.options.scales.y.grid.color);
      if (gridRadio) gridRadio.checked = chartOptionData.options.scales.y.grid.display;
      // handleInputChange("type")
    }
  }, [clickedUndotask]);

  useEffect(() => {
    if (!chartOptionData) return;

    const gridRadio = document.getElementById("showGridFormIdRadio");

    if (isVerticalStackedChart) {
      setShowGrid(chartOptionData.options.scales.y.grid.display);
      setGridColor(chartOptionData.options.scales.y.grid.color);
      if (gridRadio) gridRadio.checked = chartOptionData.options.scales.y.grid.display;
    } else if (isHorizontalStackedChart) {
      setShowGrid(chartOptionData.options.scales.x.grid.display);
      setGridColor(chartOptionData.options.scales.x.grid.color);
      if (gridRadio) gridRadio.checked = chartOptionData.options.scales.x.grid.display;
    } else if (isLineArea) {
      setShowGrid(chartOptionData.options.scales.y.grid.display);
      setGridColor(chartOptionData.options.scales.y.grid.color);
      if (gridRadio) gridRadio.checked = chartOptionData.options.scales.y.grid.display;
    }
    isInitialMountDone.current = true;
  }, []);

  return (
    <>
      <div className={cx(style["mb-3"], style["color-33"])}>
        <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
          <Input
            cssModule={style}
            returnType="noGroup"
            type="checkbox"
            className={style["form-check-input"]}
            id={`showGridFormIdRadio`}
            label="Show Grid"
            defaultChecked={showGrid}
            onChange={e => handleInputChange("showGrid", e.target.checked)}
          />
        </div>
      </div>
      {showGrid && (
        <div className={cx(style["mb-3"], style["d-flex"], style["align-items-center"])}>
          <h6 className={cx(style["color-33"], style["font-15"], style["mb-0"], style["me-3"])}>Grid Line Color</h6>

          <div className={style["custom-tooltip"]}>
            <ColorPickerWidget color={gridColor} setColor={setGridColor} moveDropdownUp={100}/>
            <div className={cx(style["custom-tooltip-content"], style["top"])}>Grid Line Color</div>
          </div>
        </div>
      )}
    </>
  );
};

Grid.propTypes = {
  setChartOptionData: PropTypes.func,
  chartDefaultData: PropTypes.object,
  chartOptionData: PropTypes.object,
  chartType: PropTypes.string,
};

const Labels = ({
  setChartOptionData,
  chartDefaultData,
  chartOptionData,
  chartType,
  labelFormat,
  setLabelFormat,
  clickedUndotask,
}) => {
  const dropdownOption = [{ name: "Value" }, { name: "Percentage" }];
  const isPieOrDonut = ["pie", "donut"].includes(chartType);
  const isInitialMountDone = useRef(false);
  const [axesLabelsFontFamily, setAxesLablesFontFamily] = useState(
    chartDefaultData?.chartConfig?.formatting?.axes?.fontFamily
  );
  const [axesLablesFontSize, setAxesLabelsFontSize] = useState(
    chartDefaultData?.chartConfig?.formatting?.axes?.fontSize
  );
  const [axesLablesColor, setAxesLablesColor] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.color);
  const [showAxesLabels, setShowAxesLables] = useState(true);
  const [axesDataLabelsFontFamily, setDataLablesFontFamily] = useState(
    chartDefaultData?.chartConfig?.formatting?.axes?.fontFamily
  );
  const [dataLablesFontSize, setDataLabelsFontSize] = useState(
    chartDefaultData?.chartConfig?.formatting?.axes?.fontSize
  );
  const [dataLablesColor, setDataLablesColor] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.color);
  const [showDataLabels, setShowDataLables] = useState();

  const customClone = obj => {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    // If it's an object, create a new one and recursively copy properties
    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    // Handle functions explicitly by copying them
    if (typeof obj === "function") {
      return obj.bind(obj); // or simply return obj if you don't need to bind
    }

    return cloned;
  };

  const handleInputChange = (type, val) => {
    let state = customClone(chartOptionData);
    if (type === "showAxeslabels") {
      setShowAxesLables(val);

      document.getElementById("axesLabelsFormIdRadio").checked = val;

      state.options.scales.x.ticks.display = val;
      state.options.scales.y.ticks.display = val;

      setChartOptionData(state);
    } else if (type === "showDatalabels") {
      setShowDataLables(val);
      document.getElementById("dataLabelsFormIdRadio").checked = val;

      state.options.plugins.datalabels.display = val;

      setChartOptionData(state);
    }
  };

  const handleLabelFormat = val => {
    setLabelFormat({ name: val });
  };

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.ticks.font.family;
    if (axesLabelsFontFamily === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.ticks.font.family = axesLabelsFontFamily;
    state.options.scales.y.ticks.font.family = axesLabelsFontFamily;
    setChartOptionData(state);
  }, [axesLabelsFontFamily]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.ticks.font.size;
    if (axesLablesFontSize === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.ticks.font.size = axesLablesFontSize;
    state.options.scales.y.ticks.font.size = axesLablesFontSize;
    setChartOptionData(state);
  }, [axesLablesFontSize]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.scales.x.ticks.color;
    if (axesLablesColor === current) return;

    let state = customClone(chartOptionData);
    state.options.scales.x.ticks.color = axesLablesColor;
    state.options.scales.y.ticks.color = axesLablesColor;
    setChartOptionData(state);
  }, [axesLablesColor]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.plugins.datalabels.font.family;
    if (axesDataLabelsFontFamily === current) return;

    let state = customClone(chartOptionData);
    state.options.plugins.datalabels.font.family = axesDataLabelsFontFamily;
    // state.options.plugins.datalabels.font.family = axesDataLabelsFontFamily;
    setChartOptionData(state);
  }, [axesDataLabelsFontFamily]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.plugins.datalabels.font.size;
    if (dataLablesFontSize === current) return;

    let state = customClone(chartOptionData);
    state.options.plugins.datalabels.font.size = dataLablesFontSize;
    // state.options.plugins.datalabels.font.size = dataLablesFontSize;
    setChartOptionData(state);
  }, [dataLablesFontSize]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.plugins.datalabels.color;
    if (dataLablesColor === current) return;

    let state = customClone(chartOptionData);
    state.options.plugins.datalabels.color = dataLablesColor;
    // state.options.plugins.datalabels.color = dataLablesColor;
    setChartOptionData(state);
  }, [dataLablesColor]);

  useEffect(() => {
    const axesLabelsRadio = document.getElementById("axesLabelsFormIdRadio");
    const dataLabelsRadio = document.getElementById("dataLabelsFormIdRadio");

    if (axesLabelsRadio) {
      axesLabelsRadio.checked = chartOptionData.options.scales.x.ticks.display;
    }

    if (dataLabelsRadio) {
      dataLabelsRadio.checked = chartOptionData.options.plugins.datalabels.display;
    }

    if (!isPieOrDonut) {
      setShowAxesLables(chartOptionData.options.scales.x.ticks.display);
      setAxesLablesFontFamily(chartOptionData.options.scales.x.ticks.font.family);
      setAxesLabelsFontSize(chartOptionData.options.scales.x.ticks.font.size);
      setAxesLablesColor(chartOptionData.options.scales.x.ticks.color);
    }
    setShowDataLables(chartOptionData.options.plugins.datalabels.display);
    setDataLablesFontFamily(chartOptionData.options.plugins.datalabels.font.family);
    setDataLabelsFontSize(chartOptionData.options.plugins.datalabels.font.size);
    setDataLablesColor(chartOptionData.options.plugins.datalabels.color);
  }, [clickedUndotask]);

  useEffect(() => {
    const axesLabelsRadio = document.getElementById("axesLabelsFormIdRadio");
    const dataLabelsRadio = document.getElementById("dataLabelsFormIdRadio");

    if (axesLabelsRadio) {
      axesLabelsRadio.checked = chartOptionData.options.scales.x.ticks.display;
    }

    if (dataLabelsRadio) {
      dataLabelsRadio.checked = chartOptionData.options.plugins.datalabels.display;
    }

    if (!isPieOrDonut) {
      setShowAxesLables(chartOptionData.options.scales.x.ticks.display);
      setAxesLablesFontFamily(chartOptionData.options.scales.x.ticks.font.family);
      setAxesLabelsFontSize(chartOptionData.options.scales.x.ticks.font.size);
      setAxesLablesColor(chartOptionData.options.scales.x.ticks.color);
    }
    setShowDataLables(chartOptionData.options.plugins.datalabels.display);
    setDataLablesFontFamily(chartOptionData.options.plugins.datalabels.font.family);
    setDataLabelsFontSize(chartOptionData.options.plugins.datalabels.font.size);
    setDataLablesColor(chartOptionData.options.plugins.datalabels.color);
    isInitialMountDone.current = true;
  }, []);

  return (
    <>
      {!isPieOrDonut && (
        <div className={style["mb-3"]}>
          <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
            <Input
              cssModule={style}
              returnType="noGroup"
              type="checkbox"
              className={style["form-check-input"]}
              id={`axesLabelsFormIdRadio`}
              label="Show Axes Labels"
              defaultChecked={showAxesLabels}
              onChange={e => handleInputChange("showAxeslabels", e.target.checked)}
            />
          </div>
        </div>
      )}
      {showAxesLabels && !isPieOrDonut && (
        <div className={style["mb-1"]}>
          <ul className={cx(style["font-size-family"], style["with-color"])}>
            <FontFamily
              defaultFontSet={axesLabelsFontFamily}
              setFontFamily={setAxesLablesFontFamily}
              sourceComponent={CHART}
            />
            <FontSize
              defaultFontSizeSet={axesLablesFontSize}
              setFontSize={setAxesLabelsFontSize}
              defaultFontList={fontSizeList}
              sourceComponent={CHART}
            />
            <li className={style["custom-tooltip"]}>
              <ColorPickerWidget color={axesLablesColor} setColor={setAxesLablesColor} moveDropdownUp={100} />
              <div className={cx(style["custom-tooltip-content"], style["top"])}>Axes Label Color</div>
            </li>
          </ul>
        </div>
      )}

      <div className={style["mb-3"]}>
        <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
          <Input
            cssModule={style}
            returnType="noGroup"
            type="checkbox"
            className={style["form-check-input"]}
            id={`dataLabelsFormIdRadio`}
            label="Show Data Labels"
            defaultChecked={showDataLabels}
            onChange={e => handleInputChange("showDatalabels", e.target.checked)}
          />
        </div>
      </div>
      {showDataLabels && (
        <div className={style["mb-1"]}>
          <ul className={cx(style["font-size-family"], style["with-color"])}>
            <FontFamily
              defaultFontSet={axesDataLabelsFontFamily}
              setFontFamily={setDataLablesFontFamily}
              sourceComponent={CHART}
            />
            <FontSize
              defaultFontSizeSet={dataLablesFontSize}
              setFontSize={setDataLabelsFontSize}
              defaultFontList={fontSizeList}
              sourceComponent={CHART}
            />
            <li className={style["custom-tooltip"]}>
              <ColorPickerWidget color={dataLablesColor} setColor={setDataLablesColor} moveDropdownUp={135}/>
              <div className={cx(style["custom-tooltip-content"], style["top"])}>Data Label Color</div>
            </li>
          </ul>
        </div>
      )}

      {showDataLabels && isPieOrDonut && (
        <>
          <div className={cx(style["d-flex"], style["flex-column"], style["caption-style-dropdown"])}>
            <h6 className={cx(style["mb-2"], style["color-33"], style["font-15"])}>Format</h6>
            <div className={style["w-75"]}>
              <Input
                type="dropdown"
                getOptionValue={option => option?.name}
                getOptionLabel={option => <span>{option?.name}</span>}
                defaultValue={labelFormat}
                options={dropdownOption}
                required={false}
                hideSearch={true}
                updateState={value => handleLabelFormat(value.name)}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

Labels.Labels = {
  setChartOptionData: PropTypes.func,
  chartDefaultData: PropTypes.object,
  chartOptionData: PropTypes.object,
  chartType: PropTypes.string,
  labelFormat: PropTypes.object,
  setLabelFormat: PropTypes.func,
};

const Legend = ({ setChartOptionData, chartDefaultData, chartOptionData, clickedUndotask }) => {
  const isInitialMountDone = useRef(false);
  const [legendFontFamily, setLegendFontFamily] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.fontFamily);
  const [legendFontSize, setLegendFontSize] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.fontSize);
  const [legendColor, setLegendColor] = useState(chartDefaultData?.chartConfig?.formatting?.axes?.color);
  const [showLegend, setShowLegend] = useState(true);

  const customClone = obj => {
    if (obj === null || typeof obj !== "object") {
      return obj; // If it's a primitive or null, just return it
    }

    // Handle special types like Date, RegExp, Map, Set
    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    // If it's an object, create a new one and recursively copy properties
    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    // Handle functions explicitly by copying them
    if (typeof obj === "function") {
      return obj.bind(obj); // or simply return obj if you don't need to bind
    }

    return cloned;
  };

  const handleInputChange = (type, val) => {
    let state = customClone(chartOptionData);
    if (type === "showLegend") {
      setShowLegend(val);

      document.getElementById("legendsFormIdRadio").checked = val;

      state.options.plugins.legend.display = val;

      setChartOptionData(state);
    }
  };

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.plugins.legend.labels.font.family;
    if (legendFontFamily === current) return;

    let state = customClone(chartOptionData);
    state.options.plugins.legend.labels.font.family = legendFontFamily;

    setChartOptionData(state);
  }, [legendFontFamily]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.plugins.legend.labels.font.size;
    if (legendFontSize === current) return;

    let state = customClone(chartOptionData);
    state.options.plugins.legend.labels.font.size = legendFontSize;

    setChartOptionData(state);
  }, [legendFontSize]);

  useEffect(() => {
    if (!isInitialMountDone.current) return;
    const current = chartOptionData.options.plugins.legend.labels.color;
    if (legendColor === current) return;

    let state = customClone(chartOptionData);
    state.options.plugins.legend.labels.color = legendColor;
    setChartOptionData(state);
  }, [legendColor]);

  useEffect(() => {
    const legendRadio = document.getElementById("legendsFormIdRadio");
    if (legendRadio) {
      legendRadio.checked = chartOptionData.options.plugins.legend.display;
    }
    setShowLegend(chartOptionData.options.plugins.legend.display);
    setLegendFontFamily(chartOptionData.options.plugins.legend.labels.font.family);
    setLegendFontSize(chartOptionData.options.plugins.legend.labels.font.size);
    setLegendColor(chartOptionData.options.plugins.legend.labels.color);
  }, [clickedUndotask]);

  useEffect(() => {
    const legendRadio = document.getElementById("legendsFormIdRadio");
    if (legendRadio) {
      legendRadio.checked = chartOptionData.options.plugins.legend.display;
    }
    setShowLegend(chartOptionData.options.plugins.legend.display);
    setLegendFontFamily(chartOptionData.options.plugins.legend.labels.font.family);
    setLegendFontSize(chartOptionData.options.plugins.legend.labels.font.size);
    setLegendColor(chartOptionData.options.plugins.legend.labels.color);
    isInitialMountDone.current = true;
  }, []);

  return (
    <>
      <div className={style["mb-3"]}>
        <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
          <Input
            cssModule={style}
            returnType="noGroup"
            type="checkbox"
            className={style["form-check-input"]}
            id={`legendsFormIdRadio`}
            label="Show Legend"
            defaultChecked={showLegend}
            onChange={e => handleInputChange("showLegend", e.target.checked)}
          />
        </div>
      </div>
      {showLegend && (
        <div className={style["mb-3"]}>
          <ul className={cx(style["font-size-family"], style["with-color"])}>
            <FontFamily defaultFontSet={legendFontFamily} setFontFamily={setLegendFontFamily} sourceComponent={CHART} />
            <FontSize
              defaultFontSizeSet={legendFontSize}
              setFontSize={setLegendFontSize}
              defaultFontList={fontSizeList}
              sourceComponent={CHART}
            />
            <li className={style["custom-tooltip"]}>
              <ColorPickerWidget color={legendColor} setColor={setLegendColor} moveDropdownUp={100} />
              <div className={cx(style["custom-tooltip-content"], style["top"])}>Legend Color</div>
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

Legend.propTypes = {
  setChartOptionData: PropTypes.func,
  chartDefaultData: PropTypes.object,
  chartOptionData: PropTypes.object,
};

const Style = ({ setChartOptionData, chartOptionData, chartDropDownValue, clickedUndotask }) => {
  const dropdownOption = [{ name: "Light" }, { name: "Dark" }];
  const isLineArea = ["area", "line"].includes(chartDropDownValue);
  const [showCaption, setShowCaption] = useState(true);
  const [showDots, setShowDots] = useState(false);
  const [captionStyle, setCaptionStyle] = useState({ name: "Light" });

  const customClone = obj => {
    if (obj === null || typeof obj !== "object") {
      return obj; // If it's a primitive or null, just return it
    }

    // Handle special types like Date, RegExp, Map, Set
    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    // If it's an object, create a new one and recursively copy properties
    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    // Handle functions explicitly by copying them
    if (typeof obj === "function") {
      return obj.bind(obj); // or simply return obj if you don't need to bind
    }

    return cloned;
  };

  const handleInputChange = (type, val) => {
    let state = customClone(chartOptionData);
    if (type === "showCaption") {
      setShowCaption(val);
      document.getElementById("showCaptionFormIdRadio").checked = val;

      state.options.plugins.tooltip.enabled = val;
    } else if (type === "showDots") {
      setShowDots(val);
      document.getElementById("showDotsFormIdRadio").checked = val;
      if (val) {
        state.options.elements.point.radius = 5;
        state.options.elements.point.hoverRadius = 5;
        state.options.plugins.legend.labels.pointStyle = "circle";
      } else {
        state.options.elements.point.radius = 0;
        state.options.elements.point.hoverRadius = 0;
        state.options.plugins.legend.labels.pointStyle = "line";
      }
    }
    setChartOptionData(state);
  };

  const handleHoverStyle = val => {
    let state = customClone(chartOptionData);
    setCaptionStyle(val);
    setCaptionStyle({ name: val });

    if (val === "Light") {
      state.options.plugins.tooltip.backgroundColor = "#ffffff";
      state.options.plugins.tooltip.titleColor = "#000000";
      state.options.plugins.tooltip.bodyColor = "#000000";
    } else {
      state.options.plugins.tooltip.backgroundColor = "#000000";
      state.options.plugins.tooltip.titleColor = "#ffffff";
      state.options.plugins.tooltip.bodyColor = "#ffffff";
    }

    setChartOptionData(state);
  };

  useEffect(() => {
    const showCaptionRadio = document.getElementById("showCaptionFormIdRadio");
    if (showCaptionRadio) {
      showCaptionRadio.checked = chartOptionData.options.plugins.tooltip.enabled;
    }
    setShowCaption(chartOptionData.options.plugins.tooltip.enabled);

    const showDotsRadio = document.getElementById("showDotsFormIdRadio");
    if (chartOptionData.options.plugins.legend.labels.pointStyle === "circle") {
      setShowDots(true);
      if (showDotsRadio) showDotsRadio.checked = true;
    } else if (chartOptionData.options.plugins.legend.labels.pointStyle === "line") {
      setShowDots(false);
      if (showDotsRadio) showDotsRadio.checked = false;
    }

    if (chartOptionData.options.plugins.tooltip.backgroundColor === "#ffffff") {
      setCaptionStyle({ name: "Light" });
    } else if (chartOptionData.options.plugins.tooltip.backgroundColor === "#000000") {
      setCaptionStyle({ name: "Dark" });
    }
  }, [clickedUndotask]);

  useEffect(() => {
    if (!chartOptionData) return; // Prevent running if data is not ready

    const showCaptionRadio = document.getElementById("showCaptionFormIdRadio");
    if (showCaptionRadio) {
      showCaptionRadio.checked = chartOptionData.options.plugins.tooltip.enabled;
    }
    setShowCaption(chartOptionData.options.plugins.tooltip.enabled);

    const showDotsRadio = document.getElementById("showDotsFormIdRadio");
    if (chartOptionData.options.plugins.legend.labels.pointStyle === "circle") {
      setShowDots(true);
      if (showDotsRadio) showDotsRadio.checked = true;
    } else if (chartOptionData.options.plugins.legend.labels.pointStyle === "line") {
      setShowDots(false);
      if (showDotsRadio) showDotsRadio.checked = false;
    }

    if (chartOptionData.options.plugins.tooltip.backgroundColor === "#ffffff") {
      setCaptionStyle({ name: "Light" });
    } else if (chartOptionData.options.plugins.tooltip.backgroundColor === "#000000") {
      setCaptionStyle({ name: "Dark" });
    }
  }, []);

  return (
    <>
      <div className={style["mb-3"]}>
        <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
          <Input
            cssModule={style}
            returnType="noGroup"
            type="checkbox"
            className={style["form-check-input"]}
            id={`showCaptionFormIdRadio`}
            label="Show Caption on Hover"
            defaultChecked={showCaption}
            onChange={e => handleInputChange("showCaption", e.target.checked)}
          />
        </div>
      </div>
      {showCaption && (
        <>
          <div className={cx(style["d-flex"], style["flex-column"], style["caption-style-dropdown"])}>
            <h6 className={cx(style["mb-2"], style["color-33"], style["font-15"])}>Caption Style</h6>
            <div className={style["w-75"]}>
              <Input
                type="dropdown"
                getOptionValue={option => option?.name}
                getOptionLabel={option => <span>{option?.name}</span>}
                defaultValue={captionStyle}
                options={dropdownOption}
                required={false}
                hideSearch={true}
                updateState={value => handleHoverStyle(value.name)}
              />
            </div>
          </div>
        </>
      )}
      {isLineArea && (
        <div className={style["mb-1"]}>
          <div className={cx(style["form-check"], style["form-switch"], style["mb-2"])}>
            <Input
              cssModule={style}
              returnType="noGroup"
              type="checkbox"
              className={style["form-check-input"]}
              id={`showDotsFormIdRadio`}
              label="Show Dots"
              defaultChecked={showDots}
              onChange={e => handleInputChange("showDots", e.target.checked)}
            />
          </div>
        </div>
      )}
    </>
  );
};

Style.propTypes = {
  setChartOptionData: PropTypes.func,
  chartOptionData: PropTypes.object,
  chartDropDownValue: PropTypes.string,
};

const RightClickedMenuBar = ({ rightContextMenuBar, setRightContextMenuBar, handleContextClick }) => {
  const rightContextRef = useRef();
  const adjustedRef = useRef(false);
  const contextMenuType = [
    { name: "Cut", icon: "ui-scissors" },
    { name: "Copy", icon: "ui-clone2" },
    { name: "Paste", icon: "ui-paste" },
    { name: "Insert Row Above", icon: "ui-insert-row-above", disable: ["cell", "column"] },
    { name: "Insert Row Below", icon: "ui-insert-row-below", disable: ["cell", "column"] },
    { name: "Insert Column Left", icon: "ui-insert-column-left", disable: ["cell", "row"] },
    { name: "Insert Column Right", icon: "ui-insert-column-right", disable: ["cell", "row"] },
    { name: "Delete Row", icon: "ui-trash", disable: ["cell", "column"] },
    { name: "Delete Column", icon: "ui-trash", disable: ["cell", "row"] },
  ];

  useClickOutsideExceptContextMenu(rightContextRef, () => {
    setRightContextMenuBar({ ...rightContextMenuBar, enable: false });
  });

  useLayoutEffect(() => {
    if (adjustedRef.current) return;
    let screenHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    let contextMenuHeight = parseFloat(window.getComputedStyle(rightContextRef.current)?.height);
    let contextMenuTop = document.querySelector(".context-menu").getBoundingClientRect().top;
    let contextMenuBottom = contextMenuTop + contextMenuHeight + 20;
    let overflowAmount = screenHeight - contextMenuBottom;

    if (overflowAmount < 0) {
      setRightContextMenuBar(prev => {
        let newTop = prev.top - Math.abs(overflowAmount);
        let newStyleTop = `${parseFloat(prev.style.top) - Math.abs(overflowAmount)}px`;

        if (newTop !== prev.top || newStyleTop !== prev.style.top) {
          adjustedRef.current = true;
          return {
            ...prev,
            top: newTop,
            style: {
              ...prev.style,
              top: newStyleTop,
            },
          };
        }
        return prev;
      });
    }
  }, [rightContextMenuBar]);

  return (
    <>
      <div className={style["context-menu"]} style={rightContextMenuBar.style} ref={rightContextRef}>
        <ul className={style["toolbar-group"]}>
          {contextMenuType?.map((contextMenu, idx) => (
            <React.Fragment key={idx}>
              <li
              // className={cx({
              //   [style["custom-tooltip"]]: !props.rightContext,
              // })}
              >
                <span
                  className={cx(style["toolset-action"], {
                    [style["disable"]]: contextMenu?.disable?.includes(rightContextMenuBar.clickedType),
                  })}
                  onClick={() => handleContextClick(contextMenu.name)}>
                  <Icon icon={contextMenu.icon} />
                  <>{contextMenu.name}</>
                </span>
                {/* {!props.rightContext && <div className={style["custom-tooltip-content"]}>Delete Column</div>} */}
              </li>

              {(contextMenu.name === "Paste" || contextMenu.name === "Insert Column Right") && <hr />}
            </React.Fragment>
          ))}
        </ul>
      </div>
    </>
  );
};

const SheetData = ({
  setChartOptionData,
  chartOptionData,
  chartDropDownValue,
  clickedUndotask,
  chartDropDownChanged,
  pieDonutExtraData,
}) => {
  const columnColorData = [
    "#4472C4",
    "#C00000",
    "#FFC000",
    "#008080",
    "#D60093",
    "#ED7D31",
    "#70AD47",
    "#5F5F5F",
    "#CC9900",
    "#FF5050",
  ];
  const sheetDataContant = {
    maxRow: 100,
    maxCol: 100,
    minRow: 1,
    minCol: 1,
  };
  let autoScrollInterval = null;
  const isSyncingChartToSheet = useRef(false);
  const isSyncingColumnColor = useRef(false);
  const selectedRowColumn = useRef();
  const chartDataRef = useRef();
  const isMultiSelection = useRef(false);
  const mouseSingleCellClick = useRef(null);
  const multipleCellSelectionRef = useRef();
  const shouldReplaceOnFirstKey = useRef(false);
  const [columnContains, setColumnContains] = useState([]);
  const [rightContextMenuBar, setRightContextMenuBar] = useState({
    enable: false,
    style: { visibility: "hidden" },
  });
  const [sheetDataContainer, setSheetDataContainer] = useState({});
  const [multipleCellSelection, setMultipleCellSelection] = useState(new Set());
  const [columnColor, setColumnColor] = useState();
  const [defaultColumnColor, setDefaultColumnColor] = useState({});
  const [eyeDropperColor, setEyeDropperColor] = useState();
  const [columnColorClicked, setColumnColorClicked] = useState();
  const [columnContainerWidth, setColumnContainerWidth] = useState();

  const customClone = obj => {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    if (typeof obj === "function") {
      return obj.bind(obj);
    }

    return cloned;
  };

  const mutipleCellDeSelect = () => {
    document.querySelectorAll(".multi-selected").forEach(cell => {
      cell.classList.remove("multi-selected");
      cell.contentEditable = "false";
    });

    const borderClasses = [
      "border-top-left",
      "border-top-right",
      "border-left-bottom",
      "border-right-bottom",
      "border-top",
      "border-left",
      "border-right",
      "border-bottom",
    ];

    document
      .querySelectorAll(borderClasses.map(cls => `.${cls}`).join(", "))
      .forEach(cell => cell.classList.remove(...borderClasses));
  };

  const inputCellDeSelect = () => {
    document.querySelectorAll(".selected").forEach(cell => {
      cell.classList.remove("selected");
      cell.contentEditable = "false";
    });
  };

  const handleInputCellClick = id => {
    if (mouseSingleCellClick.current === id) return;
    mouseSingleCellClick.current = null;

    columnRowDeSelection();
    inputCellDeSelect();
    mutipleCellDeSelect();
    const inputCellClicked = document.getElementById(id);
    inputCellClicked.classList.add("selected");

    
    inputCellClicked.focus({ preventScroll: true });
    shouldReplaceOnFirstKey.current = true;
  };

  const removeEmptyCellData = obj => {
    const result = {};

    for (const rowKey in obj) {
      const row = obj[rowKey];
      const filteredRow = {};

      for (const cellKey in row) {
        if (row[cellKey]["cell-data"] !== "") {
          filteredRow[cellKey] = row[cellKey];
        }
      }

      if (Object.keys(filteredRow).length > 0) {
        result[rowKey] = filteredRow;
      }
    }

    return result;
  };

  const buildGridFromSelection = multipleSelection => {
    let selectedCell = [...multipleSelection];

    const rows = new Set();
    const cols = new Set();

    selectedCell.forEach(cell => {
      let [row, col] = cell?.split("-");
      rows.add(Number(row));
      cols.add(col);
    });

    let sortedRow = [...rows].sort((a, b) => a - b);
    let sortedCol = [...cols];

    let grid = sortedRow.map(row =>
      sortedCol.map(col => sheetDataContainer[`row-${row}`]?.[`${row}-${col}`]?.["cell-data"] || "")
    );

    return grid;
  };

  const handleRightClick = (event, id) => {
    event.preventDefault();

    const [type, num] = id?.split("-");

    if (["Row", "Col"].includes(type)) {
      columnRowSelection(id);
    } else {
      columnRowDeSelection();
      const allSelectedCell = document.querySelectorAll(".selected");
      allSelectedCell.forEach((cell, _) => {
        cell.classList.remove("selected");
      });
    }

    let selectionType;

    const inputCellClicked = document.getElementById(id);
    inputCellClicked.classList.add("selected");

    if (inputCellClicked.classList.contains("input-cell")) selectionType = "cell";
    else if (inputCellClicked.classList.contains("row-name")) selectionType = "row";
    else if (inputCellClicked.classList.contains("column-name")) selectionType = "column";

    let chartContainer = document.querySelector(".chart-data-container").getBoundingClientRect();
    let targetCell = event.target.getBoundingClientRect();

    let contextMenuLeft = event.clientX - chartContainer.left;
    let contextMenuTop = event.clientY - chartContainer.top;

    let cellLeft = event.clientX - targetCell.left;
    let cellWidth = targetCell.width;
    let cellTop = event.clientY - targetCell.top;

    let totalExtraLeft = cellWidth - cellLeft;
    contextMenuLeft = contextMenuLeft + totalExtraLeft;
    contextMenuTop = contextMenuTop - cellTop;

    // let contextMenuLeft  = targetCell.width + targetCell.left - chartContainer.left;
    // let contextMenuTop  = targetCell.top - chartContainer.top;

    setRightContextMenuBar(prev => ({
      ...prev,
      idx: id,
      clickedType: selectionType,
      enable: true,
      top: contextMenuTop,
      left: contextMenuLeft,
      style: { ...prev.style, top: `${contextMenuTop}px`, left: `${contextMenuLeft}px`, visibility: "visible" },
    }));
  };

  const insertRowAboveBelow = id => {
    let rowNum = parseInt(id?.split("-")[1], 10);
    // let state = structuredClone(sheetDataContainer);
    let state =  JSON.parse(JSON.stringify(sheetDataContainer));
    let maxRow = Math.max(...Object.keys(state).map(key => parseInt(key?.split("-")[1], 10)));

    for (let i = maxRow; i >= rowNum; i--) {
      if (!state[`row-${i + 1}`]) state[`row-${i + 1}`] = {};

      if (Object.keys(state[`row-${i}`] ?? {}).length !== 0) {
        Object.keys(state[`row-${i}`] ?? {}).forEach((data, idx) => {
          let cellKey = data?.split("-")[1];

          if (Object.keys(state[`row-${i}`][`${i}-${cellKey}`] ?? {}).length !== 0) {
            state[`row-${i + 1}`][`${i + 1}-${cellKey}`] = state[`row-${i}`]?.[`${i}-${cellKey}`];
            state[`row-${i}`][`${i}-${cellKey}`] = {};
          }
        });
      } else state[`row-${i + 1}`] = {};
      if (i === rowNum) state[`row-${rowNum}`] = {};
    }
    setSheetDataContainer(state);
  };

  const findMaxColumn = () => {
    let column = new Set();

    Object.keys(sheetDataContainer ?? {}).forEach((row, index) => {
      Object.keys(sheetDataContainer[row] ?? {}).forEach((rowId, idx) => {
        let colCode = rowId?.split("-")[1];

        column.add(EXCEL_COLUMN_REVERSE_MAP[colCode]);
      });
    });
    const columns = [...column].sort((a, b) => a - b);
    return columns.length ? columns.pop() : 0;
  };

  const findMaxRow = () => {
    let row = new Set();

    Object.keys(sheetDataContainer ?? {}).forEach(rowKey => {
      let rowNum = rowKey?.split("-")[1];

      row.add(rowNum);
    });
    return [...row].sort().pop();
  };

  const insertColumnLeftRight = id => {
    let colCode = parseInt(id?.split("-")[1], 10);
    // let state = structuredClone(sheetDataContainer);
    let state =  JSON.parse(JSON.stringify(sheetDataContainer));
    let maxCol = findMaxColumn();

    for (let i = maxCol; i >= colCode; i--) {
      let char = EXCEL_COLUMN_MAP[i];
      Object.keys(state ?? {}).forEach((row, idx) => {
        let rowNum = row?.split("-")[1];
        Object.keys(state[row] ?? {}).forEach(rowCode => {
          if (rowCode === `${rowNum}-${char}`) {
            let nextCol = EXCEL_COLUMN_MAP[i + 1];
            state[row][`${rowNum}-${nextCol}`] = {};
            state[row][`${rowNum}-${nextCol}`] = state[row][`${rowNum}-${char}`];
            state[row][`${rowNum}-${char}`] = { "cell-data": "" };
          }
        });
      });
    }
    setSheetDataContainer(state);
  };

  const deleteSheetColumn = id => {
    let colCode = parseInt(id?.split("-")[1], 10);
    // let state = structuredClone(sheetDataContainer);
    let state =  JSON.parse(JSON.stringify(sheetDataContainer));
    let maxCol = findMaxColumn();

    for (let i = colCode; i <= maxCol; i++) {
      let char = EXCEL_COLUMN_MAP[i];

      Object.keys(state ?? {}).forEach((row, idx) => {
        let rowNum = row?.split("-")[1];
        Object.keys(state[row] ?? {}).forEach(rowCode => {
          let nextCol = EXCEL_COLUMN_MAP[i + 1];

          if (rowCode === `${rowNum}-${nextCol}`) {
            if (!state[row][`${rowNum}-${char}`]) state[row][`${rowNum}-${char}`] = {};
            state[row][`${rowNum}-${char}`] = state[row][`${rowNum}-${nextCol}`];
            state[row][`${rowNum}-${nextCol}`] = {};
          } else if (colCode === maxCol && rowCode !== `${rowNum}-${nextCol}`) state[row][`${rowNum}-${char}`] = {};
        });
      });
    }

    if (colCode <= maxCol) {
      const deleteCol = EXCEL_COLUMN_MAP[maxCol];
      Object.keys(state).forEach(rowKey => {
        Object.keys(state[rowKey]).forEach(cellKey => {
          if (cellKey.endsWith(`-${deleteCol}`)) {
            delete state[rowKey][cellKey];
          }
        });
      });
    }
    setSheetDataContainer(state);
  };

  const swapRowColumn = () => {
    // let state = structuredClone(sheetDataContainer);
    let state =  JSON.parse(JSON.stringify(sheetDataContainer));
    let maxRow = Math.max(...Object.keys(state).map(key => parseInt(key?.split("-")[1], 10)));
    let newState = {};

    for (let i = 1; i <= maxRow; i++) {
      let char = EXCEL_COLUMN_MAP[i];
      Object.keys(state[`row-${i}`] ?? {}).forEach((cellKey, index) => {
        let cellKeyCode = cellKey?.split("-")[1];
        let cellKeyNum = EXCEL_COLUMN_REVERSE_MAP[cellKeyCode];

        if (!newState[`row-${cellKeyNum}`]) newState[`row-${cellKeyNum}`] = {};
        if (!newState[`row-${cellKeyNum}`][`${cellKeyNum}-${char}`])
          newState[`row-${cellKeyNum}`][`${cellKeyNum}-${char}`] = {};

        newState[`row-${cellKeyNum}`][`${cellKeyNum}-${char}`] = state[`row-${i}`][`${i}-${cellKeyCode}`];
      });
    }
    setSheetDataContainer(newState);
  };

  const columnRowDeSelection = () => {
    const current = selectedRowColumn.current;
    if (!current) return;

    const [type, id] = current.split("-");
    if (!id) return;

    const prevHeader = document.getElementById(current);

    if (type === "Row") {
      prevHeader?.classList.remove("row-header-selected");

      const rowNum = id;
      for (let col = 1; col <= 99; col++) {
        const colCode = EXCEL_COLUMN_MAP[col];
        const rowCell = document.getElementById(`${rowNum}-${colCode}`);
        rowCell?.classList.remove("row-selected");
      }
    } else if (type === "Col") {
      prevHeader?.classList.remove("column-header-selected");

      const colCode = EXCEL_COLUMN_MAP[id];
      for (let row = 1; row <= 99; row++) {
        const cell = document.getElementById(`${row}-${colCode}`);
        cell?.classList.remove("column-selected");
      }
    }
  };

  const columnRowSelection = id => {
    columnRowDeSelection();
    inputCellDeSelect();
    mutipleCellDeSelect();

    if (!id) return;

    const [type, num] = id.split("-");
    if (!num) return;

    const headerElement = document.getElementById(id);
    if (!headerElement) return;

    if (type === "Row") {
      headerElement.classList.add("row-header-selected");

      for (let col = 1; col <= 99; col++) {
        const colCode = EXCEL_COLUMN_MAP[col];
        const rowCell = document.getElementById(`${num}-${colCode}`);
        rowCell?.classList.add("row-selected");
      }
    } else if (type === "Col") {
      const colCode = EXCEL_COLUMN_MAP[num];
      headerElement.classList.add("column-header-selected");

      for (let row = 1; row <= 99; row++) {
        const cell = document.getElementById(`${row}-${colCode}`);
        cell?.classList.add("column-selected");
      }
    }

    selectedRowColumn.current = id;
  };

  const deleteSheetRow = id => {
    let rowNum = parseInt(id?.split("-")[1], 10);
    // let updatedState = structuredClone(sheetDataContainer);
    let updatedState =  JSON.parse(JSON.stringify(sheetDataContainer));
    let maxRow = Math.max(...Object.keys(updatedState).map(key => parseInt(key?.split("-")[1], 10)));

    for (let i = rowNum; i <= maxRow; i++) {
      if (!updatedState[`row-${i}`]) updatedState[`row-${i}`] = {};

      if (Object.keys(updatedState[`row-${i + 1}`] ?? {}).length !== 0) {
        Object.keys(updatedState[`row-${i + 1}`] ?? {}).forEach((data, idx) => {
          let cellKey = data?.split("-")[1];

          if (Object.keys(updatedState[`row-${i + 1}`][`${i + 1}-${cellKey}`] ?? {}).length !== 0) {
            updatedState[`row-${i}`][`${i}-${cellKey}`] = updatedState[`row-${i + 1}`]?.[`${i + 1}-${cellKey}`];
            updatedState[`row-${i + 1}`][`${i + 1}-${cellKey}`] = {};
          }
        });
      } else updatedState[`row-${i}`] = {};
      if (i === maxRow) updatedState[`row-${maxRow}`] = {};
    }
    if (rowNum <= maxRow) {
      Object.keys(updatedState).forEach(rowKey => {
        if (rowKey === `row-${maxRow}`) {
          delete updatedState[rowKey];
        }
      });
    }
    setSheetDataContainer(updatedState);
  };

  const parseValue = value => {
    if (typeof value === "number") return value;
    if (value === "") return ""; // Preserve empty string
    const num = Number(value);
    return isNaN(num) ? value : num;
  };

  const addData = (userInput, id, typeofAddData) => {
    let rowId = id?.split("-")[0];
    let updatedUserInput = parseValue(userInput);

    const currentValue = sheetDataContainer[`row-${rowId}`]?.[id]?.["cell-data"];
    if (currentValue === updatedUserInput && !typeofAddData) return;

    if(!currentValue && updatedUserInput === "" &&!typeofAddData ) return;

    setSheetDataContainer(prev => {
      return {
        ...prev,
        [`row-${rowId}`]: {
          ...prev[`row-${rowId}`],
          [id]: {
            ...prev[`row-${rowId}`]?.[id],
            "cell-data": updatedUserInput,
          },
        },
      };
    });
  };

  const deleteCellData = cellKey => {
    let rowKey = cellKey?.split("-")[0];
    // let updatedData = structuredClone(sheetDataContainer);
    let updatedData =  JSON.parse(JSON.stringify(sheetDataContainer));

    if (updatedData[`row-${rowKey}`] && updatedData[`row-${rowKey}`][cellKey]) {
      let updatedRow = { ...updatedData[`row-${rowKey}`] };
      delete updatedRow[cellKey];

      if (Object.keys(updatedRow).length !== 0) updatedData[`row-${rowKey}`] = updatedRow;
      else delete updatedData[`row-${rowKey}`];
    }
    setSheetDataContainer(updatedData);
  };

  const deleteMultiSelectData = multipleCellSelection => {
    let newSheetDataContainer = { ...sheetDataContainer };

    multipleCellSelection.forEach(cell => {
      let row = cell?.split("-")[0];

      delete newSheetDataContainer[`row-${row}`]?.[cell];

      if (Object.keys(newSheetDataContainer[`row-${row}`] ?? {}).length === 0) {
        delete newSheetDataContainer[`row-${row}`];
      }
    });

    setSheetDataContainer(newSheetDataContainer);
    isMultiSelection.current = true;
  };

  const multipleCellUnSelect = multipleCellSelection => {
    requestAnimationFrame(() => {
      multipleCellSelection.forEach(cellId => {
        const ele = document.getElementById(cellId);
        if (ele) {
          const classList = ele.classList;
          const newClasses = new Set(["input-cell"]);

          if (classList.contains("high-lighted-text")) {
            newClasses.add("high-lighted-text");
          }
          if (classList.contains("high-lighted-text-pie")) {
            newClasses.add("high-lighted-text-pie");
          }
          ele.className = [...newClasses].join(" ");
        }
      });
    });
    setMultipleCellSelection(new Set());
  };

  const wholeRowColSelection = selectedCode => {
    // let newSheetDataContainer = structuredClone(sheetDataContainer);
    let newSheetDataContainer =  JSON.parse(JSON.stringify(sheetDataContainer));

    let [type, num] = selectedCode.split("-");
    num = Number(num);

    let tempData = new Set();

    if (type === "Row") {
      let maxCol = findMaxColumn();

      if (newSheetDataContainer[`row-${num}`]) {
        for (let i = 1; i <= maxCol; i++) {
          let colCode = EXCEL_COLUMN_MAP[i];
          tempData.add(`${num}-${colCode}`);
        }
      }
    } else {
      let colCode = EXCEL_COLUMN_MAP[num];
      let maxRow = findMaxRow();

      for (let j = 1; j <= maxRow; j++) {
        tempData.add(`${j}-${colCode}`);
      }
    }

    return tempData;
  };

  const gridToTSV = grid => grid.map(row => row.join("\t")).join("\n");

  const handleContextClick = async type => {
    selectedRowColumn.current = rightContextMenuBar.idx;
    columnRowDeSelection();
    mutipleCellDeSelect();
    inputCellDeSelect();
    if (type === "Cut") {
      const [rowColType, num] = rightContextMenuBar.idx?.split("-");
      let cutText;

      if (["Row", "Col"].includes(rowColType)) {
        let rowColSelected = wholeRowColSelection(rightContextMenuBar.idx);
        const grid = buildGridFromSelection(rowColSelected);

        cutText = gridToTSV(grid);
        deleteMultiSelectData([...rowColSelected]);
      } else if (multipleCellSelection.size !== 0) {
        const grid = buildGridFromSelection(multipleCellSelection);
        cutText = gridToTSV(grid);

        deleteMultiSelectData([...multipleCellSelection]);
        multipleCellUnSelect([...multipleCellSelection]);
        setMultipleCellSelection(new Set());
      }
      adjustWidthToText(rightContextMenuBar.idx);
      await navigator.clipboard.writeText(cutText);
    } else if (type === "Copy") {
      const [rowColType, num] = rightContextMenuBar.idx?.split("-");
      let cutText;

      if (["Row", "Col"].includes(rowColType)) {
        let rowColSelected = wholeRowColSelection(rightContextMenuBar.idx);
        const grid = buildGridFromSelection(rowColSelected);

        cutText = gridToTSV(grid);
      } else if (multipleCellSelection.size !== 0) {
        const grid = buildGridFromSelection(multipleCellSelection);

        cutText = gridToTSV(grid);
      } else {
        cutText = document.getElementById(rightContextMenuBar.idx).innerText;
      }
      await navigator.clipboard.writeText(cutText);
    } else if (type === "Paste") {
      try {
        const permission = await navigator.permissions.query({ name: "clipboard-read" });
        if (permission.state === "granted" || permission.state === "prompt") {
          const text = await navigator.clipboard.readText();
          const rows = text?.split("\n").map(row => row?.split("\t"));

          let [selectedRow, selectedCol] = rightContextMenuBar.idx?.split("-");
          let colCode = EXCEL_COLUMN_REVERSE_MAP[selectedCol];

          if (["Row"].includes(selectedRow)) {
            selectedRow = selectedCol;
            colCode = 1;
          } else if (["Col"].includes(selectedRow)) {
            selectedRow = 1;
            colCode = selectedCol;
          }

          rows.forEach((rowArr, rowIndex) => {
            rowArr.forEach((colVal, colIndex) => {
              let updatedCol = EXCEL_COLUMN_MAP[colIndex + colCode];
              let updatedRow;

              if (rowIndex === 0) updatedRow = Number(selectedRow);
              else updatedRow = rowIndex + Number(selectedRow);

              let element = document.getElementById(`${updatedRow}-${updatedCol}`);
              addData(colVal, `${updatedRow}-${updatedCol}`);

              if (element) element.innerText = colVal;
            });
          });
        }
      } catch (err) {}
    } else if (type === "Insert Row Above") {
      insertRowAboveBelow(rightContextMenuBar.idx);
    } else if (type === "Insert Row Below") {
      let rowNum = parseInt(rightContextMenuBar.idx?.split("-")[1], 10);
      insertRowAboveBelow(`$row-${rowNum + 1}`);
    } else if (type === "Delete Row") {
      deleteSheetRow(rightContextMenuBar.idx);
    } else if (type === "Insert Column Left") {
      insertColumnLeftRight(rightContextMenuBar.idx);
    } else if (type === "Insert Column Right") {
      let colNum = parseInt(rightContextMenuBar.idx?.split("-")[1], 10);
      insertColumnLeftRight(`col-${colNum + 1}`);
    } else if (type === "Delete Column") {
      deleteSheetColumn(rightContextMenuBar.idx);
    }
    setRightContextMenuBar({ ...rightContextMenuBar, enable: false });
  };

  const adjustWidthToText = id => {
    const element = document.getElementById(id);
    if (!element || id.includes("Row") || id.includes("Col")) return;

    const [row, column] = id.split("-");
    const colCode = EXCEL_COLUMN_REVERSE_MAP[column];
    const columnHeader = document.getElementById(`Col-${colCode}`);
    const prevWidth = columnHeader.offsetWidth;

    const allColumn = [];
    Object.keys(sheetDataContainer).forEach(rowKey => {
      Object.keys(sheetDataContainer[rowKey]).forEach(columnKey => {
        const [cellRow, cellColumn] = columnKey.split("-");
        if (cellColumn === column) {
          allColumn.push(columnKey);
        }
      });
    });

    let columnMaxWidth = 75;
    allColumn.forEach(cellKey => {
      const cell = document.getElementById(cellKey);
      if (!cell) return;

      const span = document.createElement("span");
      span.style.visibility = "hidden";
      span.style.position = "absolute";
      span.style.whiteSpace = "nowrap";
      span.style.font = window.getComputedStyle(cell).font;
      span.textContent = cell.textContent;

      document.body.appendChild(span);
      const width = span.offsetWidth + 10;
      document.body.removeChild(span);

      columnMaxWidth = Math.max(columnMaxWidth, width);
    });

    const tempSpan = document.createElement("span");
    tempSpan.style.visibility = "hidden";
    tempSpan.style.position = "absolute";
    tempSpan.style.whiteSpace = "nowrap";
    tempSpan.style.font = window.getComputedStyle(element).font;
    tempSpan.textContent = element.textContent;
    document.body.appendChild(tempSpan);
    const textWidth = tempSpan.offsetWidth + 10;
    document.body.removeChild(tempSpan);

    const newWidth = Math.max(textWidth, columnMaxWidth, 75);
    const diff = newWidth - prevWidth;
    const newColumnContainerWidth = columnContainerWidth + diff;

    document.getElementById(`Col-${colCode}`).style.minWidth = `${newWidth}px`;

    for (let i = 1; i <= sheetDataContant.maxCol; i++) {
      const selectedElement = document.getElementById(`${i}-${column}`);
      if (selectedElement) {
        selectedElement.style.width = `${newWidth}px`;
        selectedElement.style.maxWidth = `${newWidth}px`;
      }
    }
    setColumnContainerWidth(newColumnContainerWidth);
  };

  const handleDoubleClick = (event, id) => {
    event.preventDefault();

    mutipleCellDeSelect();

    const inputCellClicked = document.getElementById(id);
    if (inputCellClicked?.isContentEditable) return;

    const allSelectedCell = document.querySelectorAll(".selected");
    allSelectedCell.forEach(cell => {
      cell.classList.remove("selected");
      cell.contentEditable = false;
    });

    inputCellClicked.classList.add("selected");
    inputCellClicked.contentEditable = true;
    inputCellClicked.focus();

    mouseSingleCellClick.current = id;
    shouldReplaceOnFirstKey.current = false;

    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(inputCellClicked);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const handleBlurClick = (event, id) => {
    const inputCellClicked = document.getElementById(id);
    inputCellClicked.classList.remove("selected");
    inputCellClicked.contentEditable = false;

    mouseSingleCellClick.current = null;

    let userInput = inputCellClicked.innerText.trim();
    adjustWidthToText(id);
    addData(userInput, id);
  };

  const sheetAutoScroll = e => {
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval);
      autoScrollInterval = null;
    }

    const container = document.querySelector(".chart-data-container");
    const { top, right, bottom, left } = container.getBoundingClientRect();
    const scrollSpeed = 5;

    autoScrollInterval = setInterval(() => {
      let scrolled = false;

      if (e.clientY > bottom - 30) {
        container.scrollTop += scrollSpeed;
        scrolled = true;
      } else if (e.clientY < top + 35) {
        container.scrollTop -= scrollSpeed;
        scrolled = true;
      }

      if (e.clientX > right - 30) {
        container.scrollLeft += scrollSpeed;
        scrolled = true;
      } else if (e.clientX < left + 75) {
        container.scrollLeft -= scrollSpeed;
        scrolled = true;
      }

      if (!scrolled) {
        clearInterval(autoScrollInterval);
        autoScrollInterval = null;
      }
    }, 16);
  };

  const cellEventRef = useRef({
    isMouseSelected: false,
    multiSelect: new Set(),
    firstSelectedCell: null,
    cellCssBorder: {
      minRow: null,
      minCol: null,
      maxRow: null,
      maxCol: null,
    },

    start(identifier) {
      const id = identifier.id;

      if (mouseSingleCellClick.current === id) return;

      inputCellDeSelect();
      mutipleCellDeSelect();
      columnRowDeSelection();

      this.isMouseSelected = true;
      this.multiSelect.clear();
      this.multiSelect.add(id);
      this.firstSelectedCell = id;

      document.getElementById(id)?.classList.add("selected", "multi-selected");

      const allCell = document.querySelectorAll(
        ".border-top-left, .border-top-right, .border-left-bottom, .border-right-bottom, .border-top, .border-left, .border-right, .border-bottom"
      );

      allCell.forEach(cell => {
        cell.classList.remove(
          "border-top-left",
          "border-top-right",
          "border-left-bottom",
          "border-right-bottom",
          "border-top",
          "border-left",
          "border-right",
          "border-bottom"
        );
      });
    },

    mouseMove(identifier, e) {
      if (!this.isMouseSelected) return;

      sheetAutoScroll(e);

      let [startRow, startCol] = this.firstSelectedCell?.split("-");
      let [currRow, currCol] = identifier.id?.split("-");

      const startRowNum = Number(startRow);
      const currRowNum = Number(currRow);
      let minRow = Math.min(startRowNum, currRowNum);
      let maxRow = Math.max(startRowNum, currRowNum);

      let minColNum = EXCEL_COLUMN_REVERSE_MAP[startCol];
      let maxColNum = EXCEL_COLUMN_REVERSE_MAP[currCol];

      if (minColNum > maxColNum) [minColNum, maxColNum] = [maxColNum, minColNum];

      const minCol = EXCEL_COLUMN_MAP[minColNum];
      const maxCol = EXCEL_COLUMN_MAP[maxColNum];

      this.cellCssBorder = { minRow, maxRow, minCol, maxCol };

      const newSelection = new Set();
      const columnRange = Array.from({ length: maxColNum - minColNum + 1 }, (_, i) =>
        EXCEL_COLUMN_MAP[minColNum + i]
      );

      for (let i = minRow; i <= maxRow; i++) {
        columnRange.forEach(col => newSelection.add(`${i}-${col}`));
      }

      const toAdd = [...newSelection].filter(id => !this.multiSelect.has(id));
      const toRemove = [...this.multiSelect].filter(id => !newSelection.has(id));

      this.multiSelect = newSelection;

      if (toAdd.length || toRemove.length) {
        requestAnimationFrame(() => {
          toAdd.forEach(id => {
            const el = document.getElementById(id);
            if (el) el.classList.add("multi-selected");
          });

          toRemove.forEach(id => {
            const el = document.getElementById(id);
            if (el) el.classList.remove("multi-selected");
          });
        });
      }
    },

    mouseStop() {
      if (!this.isMouseSelected) return;

      if (autoScrollInterval) {
        clearInterval(autoScrollInterval);
        autoScrollInterval = null;
      }
      setMultipleCellSelection(new Set(this.multiSelect));

      this.isMouseSelected = false;
      this.firstSelectedCell = null;

      const { minRow, maxRow, minCol, maxCol } = this.cellCssBorder;

      if (minRow === maxRow && minCol === maxCol) return;

      document.getElementById(`${minRow}-${minCol}`)?.classList.add("border-top-left");
      document.getElementById(`${minRow}-${maxCol}`)?.classList.add("border-top-right");
      document.getElementById(`${maxRow}-${minCol}`)?.classList.add("border-left-bottom");
      document.getElementById(`${maxRow}-${maxCol}`)?.classList.add("border-right-bottom");

      const minColumn = EXCEL_COLUMN_REVERSE_MAP[minCol];
      const maxColumn = EXCEL_COLUMN_REVERSE_MAP[maxCol];

      for (let i = minRow; i <= maxRow; i++) {
        for (let j = minColumn; j <= maxColumn; j++) {
          const cellId = `${i}-${EXCEL_COLUMN_MAP[j]}`;
          const cell = document.getElementById(cellId);
          if (!cell) continue;

          if (i === minRow) cell.classList.add("border-top");
          if (i !== minRow && i !== maxRow && j === minColumn) cell.classList.add("border-left");
          if (i !== minRow && i !== maxRow && j === maxColumn) cell.classList.add("border-right");
          if (i === maxRow) cell.classList.add("border-bottom");
        }
      }
    },
  });

  const structureChartDataToSheet = () => {
    isSyncingChartToSheet.current = true;
    isSyncingColumnColor.current = true;

    setSheetDataContainer({});
    let defaultColor = { ...defaultColumnColor };
    if (
      ["vertical_bar", "stacked_vertical_bar", "horizontal_bar", "stacked_horizontal_bar", "line"].includes(
        chartDropDownValue
      )
    ) {
      chartOptionData.data.labels.forEach((data, idx) => {
        addData(data, `${idx + 2}-${String.fromCharCode(65)}`, "chartToSheet");
      });

      chartOptionData.data.datasets.forEach((dataSet, id) => {
        let colCode = EXCEL_COLUMN_MAP[id + 2];
        addData(dataSet.label, `${1}-${colCode}`,"chartToSheet");
        defaultColor[colCode] = dataSet.backgroundColor;

        dataSet.data.forEach((data, idx) => {
          addData(data, `${idx + 2}-${colCode}`, "chartToSheet");
        });
      });
    } else if (["area"].includes(chartDropDownValue)) {
      chartOptionData.data.labels.forEach((data, idx) => {
        addData(data, `${idx + 2}-${String.fromCharCode(65)}`,"chartToSheet");
      });

      chartOptionData.data.datasets.forEach((dataSet, id) => {
        let colCode = EXCEL_COLUMN_MAP[id + 2];
        addData(dataSet.label, `${1}-${colCode}`, "chartToSheet");

        defaultColor[colCode] = dataSet.borderColor;

        dataSet.data.forEach((data, idx) => {
          addData(data, `${idx + 2}-${colCode}`, "chartToSheet");
        });
      });
    } else if (["pie", "donut"].includes(chartDropDownValue)) {
      // let state = customClone(chartOptionData);
      let state = JSON.parse(JSON.stringify(chartOptionData));

      pieDonutExtraData.current.forEach(dataSet => {
        state.pieData.datasets.push(dataSet);
      });

      state.pieData.labels.forEach((data, idx) => {
        addData(data, `${1}-${EXCEL_COLUMN_MAP[idx + 2]}`, "chartToSheet");
      });

      state.pieData.datasets.forEach((dataSet, id) => {
        addData(dataSet.label, `${id + 2}-${String.fromCharCode(65)}`, "chartToSheet");

        dataSet.data.forEach((data, idx) => {
          let colCode = EXCEL_COLUMN_MAP[idx + 2];
          defaultColor[colCode] = dataSet.backgroundColor[idx];
          addData(data, `${id + 2}-${colCode}`, "chartToSheet");
        });
      });
    }
    setDefaultColumnColor(prev => ({ ...prev, ...defaultColor }));
  };

  // const cleanChartConfig = config => {
  //   config = customClone(config);

  //   if (Array.isArray(config.data?.labels)) {
  //     config.data.labels = config.data.labels.map(label => (label === undefined ? "" : label));
  //   }

  //   if (Array.isArray(config.data?.datasets)) {
  //     config.data.datasets = config.data.datasets.filter(dataset => {
  //       const label = dataset.label ?? "";
  //       const dataArray = Array.isArray(dataset.data) ? dataset.data : [];

  //       const hasValidLabel = label !== "";
  //       const hasValidData = dataArray.some(d => d !== "" && d !== null && d !== undefined);

  //       return hasValidLabel || hasValidData;
  //     });

  //     config.data.datasets.forEach(dataset => {
  //       if (dataset.label === undefined) {
  //         dataset.label = "";
  //       }
  //     });
  //   }

  //   if (Array.isArray(config.pieData?.labels)) {
  //     config.pieData.labels = config.pieData.labels.filter(label => label !== "");
  //   }

  //   return config;
  // };

  const structureSheetDataToChart = () => {
    // const state = customClone(chartOptionData);
    const state = JSON.parse(JSON.stringify(chartOptionData));
    // let sheetDataState = structuredClone(sheetDataContainer);
    let sheetDataState =  JSON.parse(JSON.stringify(sheetDataContainer));
    // sheetDataState = removeEmptyCellData(sheetDataState);
    const maxCol = findMaxColumn();
    const maxRowKeys = Object.keys(sheetDataState);
    const maxRow = maxRowKeys.length ? Math.max(...maxRowKeys.map(key => parseInt(key?.split("-")[1], 10))) : 0;

    const dataLabels = [];
    const dataSetLables = [];
    const pieDataLabels = [];
    const pieDataSetLabels = [];
    const pieExtra = [];

    for (let col = 2; col <= maxCol; col++) {
      const columnLetter = EXCEL_COLUMN_MAP[col];
      const dataset = {
        label: "",
        data: [],
        backgroundColor: "",
        borderColor: "",
        borderWidth: 1,
        fill: false,
      };

      for (let row = 1; row <= maxRow; row++) {
        const cellKey = `${row}-${columnLetter}`;
        const rowKey = `row-${row}`;
        const cell = sheetDataState[rowKey]?.[cellKey];

        if (row === 1) dataset.label = cell?.["cell-data"] ?? "";
        else dataset.data.push(cell?.["cell-data"] ?? "");
      }

      if (["area"].includes(chartDropDownValue)) {
        dataset.backgroundColor = defaultColumnColor[columnLetter] + "4D";
        dataset.borderColor = defaultColumnColor[columnLetter];
      } else {
        dataset.backgroundColor = defaultColumnColor[columnLetter];
        dataset.borderColor = defaultColumnColor[columnLetter];
      }

      dataSetLables.push(dataset);
    }

    dataSetLables.forEach((dataset, idx) => {
      if (state.data.datasets[idx]) {
        state.data.datasets[idx] = {
          ...state.data.datasets[idx],
          label: dataset.label,
          data: dataset.data,
          backgroundColor: dataset.backgroundColor,
          borderColor: dataset.borderColor,
        };
      } else {
        state.data.datasets.push({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: dataset.backgroundColor,
          borderColor: dataset.borderColor,
          borderWidth: 1,
          fill: false,
        });
      }
    });

    for (let row = 2; row <= maxRow; row++) {
      const cell = sheetDataState[`row-${row}`]?.[`${row}-A`];
      dataLabels.push(cell?.["cell-data"] ?? "");
    }

    state.data.labels = dataLabels;
    state.data.datasets = dataSetLables;

    const dataset = {
      label: "",
      data: [],
      backgroundColor: [],
      borderColor: "",
      borderWidth: 1,
    };

    for (let row = 1; row <= 2; row++) {
      for (let col = 1; col <= maxCol; col++) {
        const columnLetter = EXCEL_COLUMN_MAP[col];
        const cellKey = `${row}-${columnLetter}`;
        const rowKey = `row-${row}`;
        const cell = sheetDataState[rowKey]?.[cellKey];

        if (row === 1 && col !== 1) {
          const cell = sheetDataState[`row-${row}`]?.[`${row}-${columnLetter}`];
          pieDataLabels.push(cell?.["cell-data"] ?? "");
        } else if (row === 2 && col === 1) {
          dataset.label = cell?.["cell-data"];
        } else if (row === 2 && col > 1) {
          const cell = sheetDataState[`row-${row}`]?.[`${row}-${columnLetter}`];

          dataset.backgroundColor.push(defaultColumnColor[columnLetter]);
          dataset.data.push(cell?.["cell-data"]);
        }
      }
    }

    pieDataSetLabels.push(dataset);

    state.pieData.labels = pieDataLabels;
    state.pieData.datasets = pieDataSetLabels;

    if (Object.keys(sheetDataState).length !== 0) {
      for (let row = 3; row <= maxRow; row++) {
        const datasetExtra = {
          label: "",
          data: [],
          backgroundColor: [],
          borderColor: "",
          borderWidth: 1,
        };
        for (let col = 1; col <= maxCol; col++) {
          const columnLetter = EXCEL_COLUMN_MAP[col];
          const cellKey = `${row}-${columnLetter}`;
          const rowKey = `row-${row}`;
          const cell = sheetDataState[rowKey]?.[cellKey];

          if (col === 1) {
            datasetExtra.label = cell?.["cell-data"];
          } else if (col > 1) {
            const cell = sheetDataState[`row-${row}`]?.[`${row}-${columnLetter}`];

            datasetExtra.backgroundColor.push(defaultColumnColor[columnLetter]);
            datasetExtra.data.push(cell?.["cell-data"]);
          }
        }
        pieExtra.push(datasetExtra);
      }
      pieDonutExtraData.current = pieExtra;
    } else if (Object.keys(sheetDataState).length === 0) {
      if (isMultiSelection.current) {
        pieDonutExtraData.current = [];
        isMultiSelection.current = false;
      }
    }
    // const cleanedConfig = cleanChartConfig(state);
    setChartOptionData(state);
  };

   const isFirefox = () => navigator.userAgent.toLowerCase().indexOf('firefox') > -1

  const handleCut = async id => {
    let cutText = "";

    if (["Row", "Col"].includes(id?.split("-")[0])) {
      const rowColSelected = wholeRowColSelection(id);
      const grid = buildGridFromSelection(rowColSelected);
      cutText = gridToTSV(grid);
      deleteMultiSelectData([...rowColSelected]);
    } else if (multipleCellSelectionRef.current.size > 0) {
      const grid = buildGridFromSelection(multipleCellSelectionRef.current);
      cutText = gridToTSV(grid);
      deleteMultiSelectData([...multipleCellSelectionRef.current]);
      multipleCellUnSelect([...multipleCellSelectionRef.current]);
      multipleCellSelectionRef.current = new Set();
    }

    if(isFirefox()){
      let cell = document.getElementById(id);
      cell.contentEditable = false
    }

    await navigator.clipboard.writeText(cutText);
  };

  const handleCopy = async id => {
    let copyText = "";

    if (["Row", "Col"].includes(id?.split("-")[0])) {
      const rowColSelected = wholeRowColSelection(id);
      const grid = buildGridFromSelection(rowColSelected);
      copyText = gridToTSV(grid);
    } else if (multipleCellSelectionRef.current.size > 0) {
      const grid = buildGridFromSelection(multipleCellSelectionRef.current);
      copyText = gridToTSV(grid);
    } else {
      copyText = document.getElementById(id)?.innerText || "";
    }

    await navigator.clipboard.writeText(copyText);
  };

  const handlePasteData = (text, id) => {
    const rows = text?.split("\n").map(row => row?.split("\t"));
    let [selectedRow, selectedCol] = id?.split("-");
    let colCode = EXCEL_COLUMN_REVERSE_MAP[selectedCol];

    if (selectedRow === "Row") {
      selectedRow = selectedCol;
      colCode = 1;
    } else if (selectedRow === "Col") {
      selectedRow = 1;
      colCode = selectedCol;
    }

    rows.forEach((rowArr, rowIndex) => {
      rowArr.forEach((colVal, colIndex) => {
        const updatedCol = EXCEL_COLUMN_MAP[colIndex + Number(colCode)];
        const updatedRow = Number(selectedRow) + rowIndex;
        const cellId = `${updatedRow}-${updatedCol}`;
        const el = document.getElementById(cellId);
        addData(colVal, cellId);
        if (el) el.innerText = colVal;
      });
    });
  };

  const getPlatform = () => {
    if (navigator.userAgentData && navigator.userAgentData.platform) {
      const platform = navigator.userAgentData.platform;
      if (platform.includes("Mac")) return "mac";
    }

    const platformFallback = navigator.userAgent || navigator.platform || "";
    if (/Mac/i.test(platformFallback)) return "mac";

    return "unknown";
  };

  function getBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("safari") && !ua.includes("chrome")) return "safari";
    if (ua.includes("chrome")) return "chrome";
    if (ua.includes("firefox")) return "firefox";
    return "other";
  }

  const handleNavigationKeys = (event, id) => {
    const navKeys = ["Enter", "Tab"];
    if (!navKeys.includes(event.key)) return;

    event.preventDefault();

    let [row, col] = id?.split("-");
    row = Number(row);
    let updatedRow = row;
    let updatedColCode = col;

    switch (event.key) {
      case "Enter":
        if (row + 1 >= sheetDataContant.maxRow) return;
        updatedRow = row + 1;
        break;
      case "Tab":
        {
          let colCode = EXCEL_COLUMN_REVERSE_MAP[col];
          if (colCode + 1 >= sheetDataContant.maxCol) return;
          updatedColCode = EXCEL_COLUMN_MAP[colCode + 1];
        }
        break;
    }

    const currentCell = document.getElementById(id);
    currentCell.classList.remove("selected");
    currentCell.contentEditable = false;

    const nextId = `${updatedRow}-${updatedColCode}`;
    const nextCell = document.getElementById(`${updatedRow}-${updatedColCode}`);
    if (!nextCell) return;

    handleInputCellClick(nextId);
  };

 

  const handleKeyDownWindows = (event, id, cellKeyDown) => {
    const cell = document.getElementById(id);
    const key = event.key;
    const isPrintableKey = key.length === 1 && !event.ctrlKey && !event.altKey;

    if(event.ctrlKey && isFirefox() && cellKeyDown){
      cell.contentEditable = true;
    }

    if (
      shouldReplaceOnFirstKey.current &&
      (event.key === "Backspace" || event.key === "Shift" || event.key === "CapsLock")
    ) {
      event.preventDefault();
      shouldReplaceOnFirstKey.current = true;
      return;
    }

    if (shouldReplaceOnFirstKey.current && isPrintableKey) {
      event.preventDefault();

      cell.innerText = "";

      cell.contentEditable = true;
      cell.focus();

      if (key.length === 1) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(cell);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        document.execCommand("insertText", false, key);
      }
      shouldReplaceOnFirstKey.current = false;
      return;
    }

    shouldReplaceOnFirstKey.current = false;

    if (event.key?.toLowerCase() === "delete" || event.code?.toLowerCase() === "delete") {
      if (multipleCellSelectionRef.current.size > 0) {
        deleteMultiSelectData([...multipleCellSelectionRef.current]);
        multipleCellUnSelect([...multipleCellSelectionRef.current]);
        multipleCellSelectionRef.current = new Set();
      } else {
        deleteCellData(id);
      }
      if (cell.isContentEditable) cell.contentEditable = false;
    }

    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
        case "x":
          event.preventDefault();
          handleCut(id);
          break;
        case "c":
          event.preventDefault();
          columnRowDeSelection();
          mutipleCellDeSelect();
          inputCellDeSelect();
          handleCopy(id);
          break;
      }
      return;
    }

    handleNavigationKeys(event, id);
  };

  const handleKeyDownMac = (event, id, cellKeyDown) => {
    const cell = document.getElementById(id);
    if (!cell) return;

    const key = event.key;
    const isPrintableKey = key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    const isCellEditing = cell.isContentEditable && document.activeElement === cell;

    if ((event.ctrlKey || event.metaKey) && isFirefox() && cellKeyDown) {
      cell.contentEditable = true;
    }

    if (shouldReplaceOnFirstKey.current) {
      if (key === "Backspace") {
        event.preventDefault();
        cell.innerText = "";
        cell.contentEditable = true;
        cell.focus();

        deleteCellData(id);
        shouldReplaceOnFirstKey.current = false;
        return;
      }

      if (isPrintableKey) {
        event.preventDefault();
        cell.innerText = "";
        cell.contentEditable = true;
        cell.focus();

        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(cell);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand("insertText", false, key);

        shouldReplaceOnFirstKey.current = false;
        return;
      }
    }

    if (isCellEditing) {
      if (key === "Backspace") {
        const selection = window.getSelection();
        if (selection && selection.isCollapsed) {
          const range = selection.getRangeAt(0);
          if (range.startOffset === 0) {
            event.preventDefault();
            return;
          }
        }
        return;
      }

      if (isPrintableKey) {
        return;
      }
    }

    if (key === "Backspace" && multipleCellSelectionRef.current.size > 0) {
      event.preventDefault();
      deleteMultiSelectData([...multipleCellSelectionRef.current]);
      multipleCellUnSelect([...multipleCellSelectionRef.current]);
      multipleCellSelectionRef.current = new Set();
      return;
    }

    if (event.metaKey || event.ctrlKey) {
      switch (key.toLowerCase()) {
        case "x":
          event.preventDefault();
          handleCut(id);
          break;
        case "c":
          event.preventDefault();
          columnRowDeSelection();
          mutipleCellDeSelect();
          inputCellDeSelect();
          handleCopy(id);
          break;
      }
      return;
    }

    handleNavigationKeys(event, id);

    shouldReplaceOnFirstKey.current = false;
  };

  const handleKeyDown = (event, id, cellKeyDown) => {
    const platform = getPlatform();
    if (platform === "mac") {
      handleKeyDownMac(event, id, cellKeyDown);
    } else {
      handleKeyDownWindows(event, id, cellKeyDown);
    }
  };

  const generateColumnName = num => {
    let str = "";
    while (num > 0) {
      let rem = (num - 1) % 26;
      str = String.fromCharCode(65 + rem) + str;
      num = Math.floor((num - 1) / 26);
    }
    return str;
  };

  const getCellClasses = (row, col) => {
    const cellId = `${row}-${col}`;
    const isSelected = multipleCellSelection.has(cellId);

    return cx(
      "input-cell",
      // isSelected && "selected",
      // isSelected && !multipleCellSelection.has(`${row - 1}-${col}`) && "top-neighbor",
      // isSelected && !multipleCellSelection.has(`${row + 1}-${col}`) && "bottom-neighbor",
      // isSelected && !multipleCellSelection.has(`${row}-${col - 1}`) && "left-neighbor",
      // isSelected && !multipleCellSelection.has(`${row}-${col + 1}`) && "right-neighbor",
      row === 1 && "high-lighted-text",
      col === "A" && !["pie", "donut"].includes(chartDropDownValue) && "high-lighted-text",
      row === 2 && col === "A" && ["pie", "donut"].includes(chartDropDownValue) && "high-lighted-text",
      row > 2 && ["pie", "donut"].includes(chartDropDownValue) && "high-lighted-text-pie"
    );
  };

  const memoizedCells = useMemo(() => {
    return columnContains.map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} id={`row-${rowIndex + 1}`} className={cx(style["cell-row"])}>
        {columnContains.map((col, _) => (
          <div
            tabIndex={0}
            onDoubleClick={e => handleDoubleClick(e, `${rowIndex + 1}-${col}`)}
            onContextMenu={e => handleRightClick(e, `${rowIndex + 1}-${col}`)}
            key={`${rowIndex}-${col}`}
            id={`${rowIndex + 1}-${col}`}
            className={getCellClasses(rowIndex + 1, col)}
            onClick={e => {
              e.currentTarget.focus();
              handleInputCellClick(`${rowIndex + 1}-${col}`);
            }}
            onBlur={e => handleBlurClick(e, `${rowIndex + 1}-${col}`)}
            onKeyDown={e => handleKeyDown(e, `${rowIndex + 1}-${col}`, "cellKeyDown")}
            onPaste={e => {
              e.preventDefault();
              const text = e.clipboardData.getData("text/plain");
              handlePasteData(text, `${rowIndex + 1}-${col}`);
            }}
            suppressContentEditableWarning={true}
            onMouseDown={e => {
              cellEventRef.current.start({ id: `${rowIndex + 1}-${col}` }, e);
            }}
            onMouseMove={e => {
              cellEventRef.current.mouseMove({ id: `${rowIndex + 1}-${col}` }, e);
            }}
            onMouseUp={e => {
              cellEventRef.current.mouseStop({ id: `${rowIndex + 1}-${col}` }, e);
            }}>
            {sheetDataContainer[`row-${rowIndex + 1}`]?.[`${rowIndex + 1}-${col}`]?.["cell-data"]}
          </div>
        ))}
      </div>
    ));
  }, [sheetDataContainer]);

  useEffect(() => {
    if (isSyncingChartToSheet.current) {
      isSyncingChartToSheet.current = false;
      return;
    }
    structureSheetDataToChart();
  }, [sheetDataContainer]);

  useEffect(() => {
    if (eyeDropperColor) {
      setColumnColor(eyeDropperColor);
    }
  }, [eyeDropperColor]);

  useEffect(() => {
    structureChartDataToSheet();
  }, [clickedUndotask]);

  useEffect(() => {
    if (isSyncingColumnColor.current) {
      isSyncingColumnColor.current = false;
      return;
    }
    structureChartDataToSheet();
  }, [defaultColumnColor]);

  useEffect(() => {
    structureChartDataToSheet();
  }, [chartOptionData]);

  useEffect(() => {
    multipleCellSelectionRef.current = multipleCellSelection;
  }, [multipleCellSelection]);

  useEffect(() => {
    // let state = customClone(chartOptionData);
    let state = JSON.parse(JSON.stringify(chartOptionData));
    let colNum = EXCEL_COLUMN_REVERSE_MAP[columnColorClicked];
    let maxCol = findMaxColumn();

    if (columnColorClicked && state.data.datasets[colNum - 2]) {
      state.data.datasets[colNum - 2]["backgroundColor"] = columnColor;
      state.data.datasets[colNum - 2]["borderColor"] = columnColor;
    }
    let backgroundColor = [];
    let newPieDonutExtaData = JSON.parse(JSON.stringify(pieDonutExtraData.current) ?? {});

    for (let i = 1; i <= maxCol; i++) {
      if (i === colNum - 1) {
        backgroundColor.push(columnColor);
      } else {
        let colCode = EXCEL_COLUMN_MAP[i + 1];
        backgroundColor.push(defaultColumnColor[colCode]);
      }
      state.pieData.datasets[0]["backgroundColor"] = backgroundColor;
      state.pieData.datasets[0]["borderColor"] = backgroundColor;
    }
    newPieDonutExtaData.map(pieExtra => {
      pieExtra["backgroundColor"] = backgroundColor;
      pieExtra["borderColor"] = backgroundColor;
    });

    pieDonutExtraData.current = newPieDonutExtaData;

    if (["area"].includes(chartDropDownValue)) {
      if (columnColorClicked && state.data.datasets[colNum - 2]) {
        state.data.datasets[colNum - 2]["backgroundColor"] = columnColor + "4D";
        state.data.datasets[colNum - 2]["borderColor"] = columnColor;
        setChartOptionData(state);
      }
    }
    setChartOptionData(state);
    setDefaultColumnColor(prev => ({ ...prev, [columnColorClicked]: columnColor }));
  }, [columnColor]);

  useEffect(() => {
    multipleCellUnSelect([...multipleCellSelection]);
    columnRowDeSelection();
    inputCellDeSelect();
    mutipleCellDeSelect();
    structureChartDataToSheet();
  }, [chartDropDownChanged]);

  useEffect(() => {
    let newColumns = [];
    let newColorMap = {};
    for (let i = 1; i < 100; i++) {
      const colName = generateColumnName(i);
      newColumns.push(colName);
      if (i !== 1) {
        let colorCode = ((i - 3) % columnColorData.length) + 1;
        newColorMap[colName] = columnColorData[colorCode === 10 ? 0 : colorCode];
      }
    }
    let containerWidth = 75 * 99;
    setColumnContainerWidth(containerWidth);
    setDefaultColumnColor(newColorMap);
    setColumnContains(newColumns);
  }, []);

  useEffect(() => {
    let chartScrollEle = chartDataRef.current;
    if (!chartScrollEle) return;

    let lastScrollTop = 0;
    let lastScrollLeft = 0;

    const handleScroll = () => {
      const st = chartScrollEle.scrollTop;
      const sl = chartScrollEle.scrollLeft;

      if (st > lastScrollTop || sl > lastScrollLeft || st < lastScrollTop || sl < lastScrollLeft) {
        if (columnColorClicked !== null) setColumnColorClicked(null);
      }
      lastScrollTop = st;
      lastScrollLeft = sl;
    };
    chartScrollEle.addEventListener("scroll", handleScroll);

    return () => {
      chartScrollEle.removeEventListener("scroll", handleScroll);
      if (autoScrollInterval) {
        clearInterval(autoScrollInterval);
      }
    };
  }, []);

  return (
    <div
      className={cx(
        style["d-flex"],
        style["align-items-center"],
        style["map-table-section"],
        style["position-relative"]
      )}>
      <div className={cx(style["swap-row-column"], style["custom-tooltip"])} onClick={() => swapRowColumn()}>
        <Icon icon="ui-swap-row-column" />
        <div className={cx(style["custom-tooltip-content"], style["mt-5"])}>Swap Row/Column</div>
      </div>
      <div
        ref={chartDataRef}
        className={cx(style["chart-data-container"], style["customScroll"], style["scroll-X"], style["scroll-Y"])}>
        <div className={cx(style["column-container"])} style={{ width: `${columnContainerWidth}px` }}>
          {columnContains.map((col, index) => (
            <div
              key={`col-${index}`}
              id={`Col-${index + 1}`}
              className={cx(style["column-name"])}
              onContextMenu={e => handleRightClick(e, `Col-${index + 1}`)}
              tabIndex={0}
              onKeyDown={e => handleKeyDown(e, `Col-${index + 1}`)}
              onPaste={e => {
                e.preventDefault();
                const text = e.clipboardData.getData("text/plain");
                handlePasteData(text, `Col-${index + 1}`);
              }}
              suppressContentEditableWarning={true}
              onClick={() => columnRowSelection(`Col-${index + 1}`)}>
              {index !== 0 && (
                <ColumnColorPicker
                  colCode={col}
                  setColumnColorClicked={setColumnColorClicked}
                  color={defaultColumnColor[col]}
                  setEyeDropperColor={setEyeDropperColor}
                  eyeDropperColor={eyeDropperColor}
                  setColor={setColumnColor}
                  isOpen={columnColorClicked === col}
                />
              )}
              {col}
            </div>
          ))}
        </div>
        <div className={cx(style["row-container"])}>
          {columnContains.map((_, index) => (
            <div
              key={`row-${index}`}
              id={`Row-${index + 1}`}
              className={cx(style["row-name"])}
              tabIndex={0}
              onContextMenu={e => handleRightClick(e, `Row-${index + 1}`)}
              onKeyDown={e => handleKeyDown(e, `Row-${index + 1}`)}
              onPaste={e => {
                e.preventDefault();
                const text = e.clipboardData.getData("text/plain");
                handlePasteData(text, `Row-${index + 1}`);
              }}
              suppressContentEditableWarning={true}
              onClick={() => columnRowSelection(`Row-${index + 1}`)}>
              {index + 1}
            </div>
          ))}
        </div>
        <div className={cx(style["input-cell-container"])}>{memoizedCells}</div>
      </div>
      {rightContextMenuBar.enable && (
        <RightClickedMenuBar
          rightContextMenuBar={rightContextMenuBar}
          setRightContextMenuBar={setRightContextMenuBar}
          handleContextClick={handleContextClick}
        />
      )}
    </div>
  );
};

SheetData.propTypes = {};

const Chart = ({ closeModal, modalFullScreen, setModalFullScreen, editable }) => {
  const MAX_STACK_SIZE = 50;
  const pieDonutInitialConfig = [
    {
      label: "HP",
      data: [400, 600],
      backgroundColor: ["#4472C4", "#C00000"],
      borderColor: "#ffffff",
      borderWidth: 1,
    },
    {
      label: "Acer",
      data: [300, 500],
      backgroundColor: ["#4472C4", "#C00000"],
      borderColor: "#ffffff",
      borderWidth: 1,
    },
  ];
  let { metadata, widgets, updateWidgets } = useContext(EditorContext);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const clickedUndoStack = useRef(false);
  const lastSavedChartOption = useRef(null);
  const initialChartState = useRef();
  const chartDropDownChange = useRef(false);
  const closeChartTimeoutRef = useRef();
  const pieDonutExtraData = useRef(pieDonutInitialConfig);

  const chartDropDown = useMemo(() => ({
    vertical_bar: {
      settings: [
        { name: "Axes", icon: "ui-axes" },
        { name: "Grid", icon: "ui-grid" },
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Vertical Bar",
      chart_icon: "ui-vertical-bar",
    },
    stacked_vertical_bar: {
      settings: [
        { name: "Axes", icon: "ui-axes" },
        { name: "Grid", icon: "ui-grid" },
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Stacked Vertical Bar",
      chart_icon: "ui-stacked-vertical-bar",
    },
    horizontal_bar: {
      settings: [
        { name: "Axes", icon: "ui-axes" },
        { name: "Grid", icon: "ui-grid" },
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Horizontal Bar",
      chart_icon: "ui-horizontal-bar",
    },
    stacked_horizontal_bar: {
      settings: [
        { name: "Axes", icon: "ui-axes" },
        { name: "Grid", icon: "ui-grid" },
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Stacked Horizontal Bar",
      chart_icon: "ui-stacked-horizontal-bar",
    },
    line: {
      settings: [
        { name: "Axes", icon: "ui-axes" },
        { name: "Grid", icon: "ui-table" },
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Line",
      chart_icon: "ui-line-chart",
    },
    area: {
      settings: [
        { name: "Axes", icon: "ui-axes" },
        { name: "Grid", icon: "ui-grid" },
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Area",
      chart_icon: "ui-area-chart",
    },
    pie: {
      settings: [
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-style" },
      ],
      chart_name: "Pie",
      chart_icon: "ui-pie-chart",
    },
    donut: {
      settings: [
        { name: "Labels", icon: "ui-labels" },
        { name: "Legend", icon: "ui-list" },
        { name: "Style", icon: "ui-table" },
      ],
      chart_name: "Donut",
      chart_icon: "ui-donut-chart",
    },
  }), [])

  Tooltip.positioners.cursorPosition = function (elements, eventPosition) {
    return {
      x: eventPosition.x,
      y: eventPosition.y,
    };
  };

  const chartConfig = useMemo(() => ({
    options: {
      animation: true,
      responsive: true,
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      maintainAspectRatio: false,
      devicePixelRatio: window.devicePixelRatio || 2.5,

      scales: {
        x: {
          display: true,
          border: { dash: [5, 5], display: true, color: "#000000" },
          offset: true,
          grid: {
            display: false,
            drawBorder: false,
            drawOnChartArea: true,
            drawTicks: false,
            lineWidth: 1,
            color: "#d3d3d3",
          },
          title: {
            display: true,
            text: "",
            color: "#000000",
            font: { size: 12, family: "Open Sans" },
          },
          ticks: {
            display: true,
            color: "#000000",
            font: { size: 12, family: "Open Sans" },
            padding: 10,
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          border: { dash: [5, 5], display: true, color: "#000000" },
          grid: {
            display: true,
            drawBorder: false,
            drawOnChartArea: true,
            drawTicks: false,
            lineWidth: 1,
            color: "#d3d3d3",
          },

          title: {
            display: true,
            text: "",
            color: "#000000",
            font: { size: 12, family: "Open Sans" },
          },
          ticks: {
            display: true,
            color: "#000000",
            font: { size: 12, family: "Open Sans" },
            padding: 10,
            callback: value => `${value}`,
          },
        },
      },

      plugins: {
        tooltip: {
          enabled: true,
          position: "cursorPosition",
          mode: "nearest",
          intersect: true,
          backgroundColor: "#ffffff",
          titleColor: "#000000",
          boxShadow: "0 4px 6px #00000033",
          titleFont: { size: 14, family: "Open Sans" },
          bodyFont: { size: 12, family: "Open Sans" },
          bodyColor: "#000000",
          cornerRadius: 0,
          caretSize: 5,
          padding: 10,
          callbacks: {
            label: function (context) {
              let value;
              const chartType = context.chart.config.type;

              if (chartType === "bar" && context.chart.config.options.indexAxis === "y") {
                value = context.parsed.x;
              } else if (chartType === "bar" || chartType === "line") {
                value = context.parsed.y;
              } else {
                value = context.raw;
              }
              const label = context.dataset.label || "";
              return `${label}: ${value}`;
            },
            title: function (context) {
              return context[0].label;
            },
          },
        },
        datalabels: {
          display: false,
          color: "#000000",
          font: { size: 12, family: "Open Sans" },
          align: "end",
          anchor: "end",
          formatter: value => (value !== null ? value : ""),
        },
        legend: {
          display: true,
          position: "bottom",
          onClick: (e, legendItem, legend) => {},
          labels: {
            usePointStyle: true,
            font: { size: 12, family: "Open Sans" },
            color: "#000000",
            padding: 20,
            lineWidth: 2,
            pointStyle: "rect",
          },
        },
      },

      elements: {
        line: {
          fill: false,
          tension: 0,
        },
        point: {
          radius: 0,
          hitRadius: 2,
          hoverRadius: 5,
        },
      },
    },

    data: {
      labels: ["Lenovo", "HP", "Acer"],
      datasets: [
        {
          label: "Year 1",
          data: [500, 400, 300],
          backgroundColor: "#4472C4",
          borderColor: "#4472C4",
          borderWidth: 1,
          fill: false,
        },
        {
          label: "Year 2",
          data: [700, 600, 500],
          backgroundColor: "#C00000",
          borderColor: "#C00000",
          borderWidth: 1,
          fill: false,
        },
      ],
    },

    pieData: {
      labels: ["Year 1", "Year 2"],
      datasets: [
        {
          label: "Lenovo",
          data: [500, 700],
          backgroundColor: ["#4472C4", "#C00000"],
          borderColor: "",
          borderWidth: 1,
        },
      ],
    },
  }), [])

  const chartDefaultData = structuredClone(widgetConfig?.charts);

  const [chartOptionData, setChartOptionData] = useState(chartConfig);
  const [editModeActiveTab, setEditModeActiveTab] = useState(0);
  const [editDataModeOn, setEditDataModeOn] = useState();
  const [settingsTabCurrentCaption, setSettingsTabCurrentCaption] = useState("*");
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);
  const [dataparam, setDataParam] = useState();
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [cusetomClass, setCusetomClass] = useState();
  const [chartDia, setChartDia] = useState();
  const [customCss, setCustomCss] = useState(false);
  const [chartDropDownValue, setChartDropDownValue] = useState("vertical_bar");
  const [activeSettingTab, setActiveSettingTab] = useState("Axes");
  const [isTabSelected, setIsTabSelected] = useState(false);
  const [labelFormat, setLabelFormat] = useState({ name: "Value" });
  const [undoStateLength, setUndoStateLength] = useState(0);
  const [redoStateLength, setRedoStateLength] = useState(0);
  const [clickedUndotask, setClickedUndoTask] = useState(false);
  const [chartDropDownChanged, setChartDropDownChanged] = useState(false);
  const [initComplete, setInitComplete] = useState(false);
  const [insertUpdateDisableButton, setInsertUpdateDisableButton] = useState(false);
  const [disableInsertUpdate, setDisableInsertUpdate] = useState(false);

  const { postion: getPosition } = useAlignment(addWidgetClicked, chartDia?.width, chartDia?.height, "middle-center");
  const getNewWidgetObject = useAddWidget(
    addWidgetClicked,
    CHART,
    dataparam,
    getPosition,
    chartDia?.width,
    chartDia?.height
  );
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    CHART,
    assetInnerContent,
    dataparam,
    getNewWidgetObject,
    customCss,
    false,
    cusetomClass
  );

  const customClone = useCallback(obj => {
    if (obj === null || typeof obj !== "object") {
      return obj; // If it's a primitive or null, just return it
    }

    // Handle special types like Date, RegExp, Map, Set
    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (obj instanceof RegExp) {
      return new RegExp(obj);
    }

    if (obj instanceof Map) {
      return new Map([...obj].map(([key, value]) => [customClone(key), customClone(value)]));
    }

    if (obj instanceof Set) {
      return new Set([...obj].map(value => customClone(value)));
    }

    if (Array.isArray(obj)) {
      return obj.map(item => customClone(item));
    }

    // If it's an object, create a new one and recursively copy properties
    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = customClone(obj[key]);
      }
    }

    // Handle functions explicitly by copying them
    if (typeof obj === "function") {
      return obj.bind(obj); // or simply return obj if you don't need to bind
    }

    return cloned;
  }, []);

  const updateChartDropDown = value => {
    chartDropDownChange.current = true;
    setChartDropDownValue(value);
  };

  const toggleTab = tab => {
    setIsTabSelected(!isTabSelected);
    if (tab && activeSettingTab !== tab) setActiveSettingTab(tab);
  };

  const insertChart = () => {
    setInsertUpdateDisableButton(true);
    if (closeChartTimeoutRef.current) clearTimeout(closeChartTimeoutRef.current);
    const chartBasedConfig = getChartConfig(
      chartDropDownValue,
      { ...chartOptionData.data },
      chartOptionData,
      labelFormat
    );
    const imageSrc = getChartCanvasPNGSrc("chartWrap");

    let fontFamilysArray = new Set();

    fontFamilysArray.add(chartBasedConfig?.options?.scales?.x?.title?.font?.family)
    fontFamilysArray.add(chartBasedConfig?.options?.scales?.x?.ticks?.font?.family)
    fontFamilysArray.add(chartBasedConfig?.options?.plugins?.datalabels?.font?.family)
    fontFamilysArray.add(chartBasedConfig?.options?.plugins?.legend?.labels?.font?.family);

    fontFamilysArray = [...fontFamilysArray]

    let dataAttr = { ...widgetConfig[CHART].dataAttr };

    setChartDia({ height: 303, width: 468 });

    let newChart;

    if (!["pie", "donut"].includes(chartDropDownValue))
      newChart = {
        chartType: chartDropDownValue,
        pieDonutConfig: pieDonutExtraData.current,
        chartBasedConfig: chartBasedConfig,
        chartDataImage: imageSrc,
        fontFamilyDetails: fontFamilysArray
      };
    else
      newChart = {
        chartType: chartDropDownValue,
        chartBasedConfig: chartBasedConfig,
        pieDonutConfig: pieDonutExtraData.current,
        labelFormat: labelFormat,
        chartDataImage: imageSrc,
        fontFamilyDetails: fontFamilysArray
      };

    setAssetInnerContent(newChart);
    setDataParam(dataAttr);
    setAddWidgetClicked(true);

    closeChartTimeoutRef.current = setTimeout(() => {
      closeModal();
      setModalFullScreen(false);
    }, 1000);
  };

  const removeLastElement = arr => {
    if (arr.length === 0) return { newData: null, arr: [] };

    const newArr = [...arr];
    const newData = newArr.pop();
    return { newData, arr: newArr };
  };

  const handleUndoTask = () => {
    setClickedUndoTask(prev => !prev);

    const { newData: latestUndoState, arr: updatedUndoStack } = removeLastElement(undoStack.current);
    redoStack.current.push(customClone(latestUndoState));

    if (updatedUndoStack.length > 0) {
      undoStack.current = updatedUndoStack;
      const chartStateToRestore = updatedUndoStack[updatedUndoStack.length - 1];
      setChartOptionData(chartStateToRestore.chartOptionData);
      setChartDropDownValue(chartStateToRestore.chartTypeDropDown);
      pieDonutExtraData.current = chartStateToRestore.pieExtraData;
      setUndoStateLength(undoStack.current.length);
    } else {
      setChartOptionData(initialChartState.current.chartOptionData);
      setChartDropDownValue(initialChartState.current.chartTypeDropDown);
      pieDonutExtraData.current = initialChartState.current.pieExtraData;
      undoStack.current = [];
      setUndoStateLength(0);
    }

    clickedUndoStack.current = true;
    setRedoStateLength(redoStack.current.length);
  };

  const handleRedoTask = () => {
    if (redoStack.current.length === 0) {
      setRedoStateLength(0);
      return;
    }
    setClickedUndoTask(prev => !prev);

    const { newData: latestRedoState, arr: updatedRedoStack } = removeLastElement(redoStack.current);

    undoStack.current.push(customClone(latestRedoState));
    setChartOptionData(latestRedoState.chartOptionData);
    setChartDropDownValue(latestRedoState.chartTypeDropDown);
    pieDonutExtraData.current = latestRedoState.pieExtraData;
    redoStack.current = updatedRedoStack;

    if (updatedRedoStack.length === 0) {
      setRedoStateLength(0);
    }
    clickedUndoStack.current = true;
    setUndoStateLength(undoStack.current.length);
  };

  const updateChart = () => {
    setInsertUpdateDisableButton(true);
    let targetWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);
    let chartInner = document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`);
    const canvas = chartInner.querySelector("canvas");

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const styleWidth = canvas.offsetWidth;
    const styleHeight = canvas.offsetHeight;

    const chartBasedConfig = getChartConfig(
      chartDropDownValue,
      { ...chartOptionData.data },
      chartOptionData,
      labelFormat
    );

    const imageSrc = getChartCanvasPNGSrc("chartWrap");

    let fontFamilysArray = new Set();

    fontFamilysArray.add(chartBasedConfig?.options?.scales?.x?.title?.font?.family)
    fontFamilysArray.add(chartBasedConfig?.options?.scales?.x?.ticks?.font?.family)
    fontFamilysArray.add(chartBasedConfig?.options?.plugins?.datalabels?.font?.family)
    fontFamilysArray.add(chartBasedConfig?.options?.plugins?.legend?.labels?.font?.family);

    fontFamilysArray = [...fontFamilysArray]

    let newChart;
    if (!["pie", "donut"].includes(chartDropDownValue))
      newChart = {
        chartType: chartDropDownValue,
        pieDonutConfig: pieDonutExtraData.current,
        chartBasedConfig: chartBasedConfig,
        chartDataImage: imageSrc,
        fontFamilyDetails: fontFamilysArray
      };
    else
      newChart = {
        chartType: chartDropDownValue,
        chartBasedConfig: chartBasedConfig,
        pieDonutConfig: pieDonutExtraData.current,
        labelFormat: labelFormat,
        chartDataImage: imageSrc,
        fontFamilyDetails: fontFamilysArray
      };
    let widgetObject = widgets[targetWidgetIndex];

    let newArray = Object.assign([...widgets], {
      [targetWidgetIndex]: {
        ...widgetObject,
        style: {
          ...widgetObject.style,
          // height: canvasHeight,
          // width: canvasWidth,
        },
        chartConfig: newChart,
        data: {
          ...widgetObject.data,
          "data-version": "5.0",
          "data-chart-type": chartDropDownValue,
        },
        chartThumb: imageSrc,
        innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
      },
    });

    updateWidgets(newArray);
    closeModal();
    setModalFullScreen(false);
  };

  useEffect(() => {
    chartDropDownChange.current = false;
    if (clickedUndoStack.current) return;

    if (!initComplete && editable) return;

    const isVerticalBar = ["vertical_bar"].includes(chartDropDownValue);
    const isStackedVerticalBar = ["stacked_vertical_bar"].includes(chartDropDownValue);
    const isHorizontalBar = ["horizontal_bar"].includes(chartDropDownValue);
    const isStackedHorizontalBar = ["stacked_horizontal_bar"].includes(chartDropDownValue);
    const isLine = ["line"].includes(chartDropDownValue);
    const isArea = ["area"].includes(chartDropDownValue);
    const isPieOrDonut = ["pie", "donut"].includes(chartDropDownValue);
    const state = customClone(chartOptionData);

    // let baseState = {
    //   labels: [...chartConfig.data.labels],
    //   datasets: [...chartConfig.data.datasets],
    // };

    // let piDonutBaseState = {
    //   labels: [...chartConfig.pieData.labels],
    //   datasets: [...chartConfig.pieData.datasets],
    // };

    // state.data = baseState;
    // state.pieData = piDonutBaseState;
    state.options = customClone(chartConfig.options);

    if (!isPieOrDonut) {
      state.options.scales.x.grid.color = "#d3d3d3";
      state.options.scales.y.grid.color = "#d3d3d3";
      state.options.scales.x.title.text = "";
      state.options.scales.y.title.text = "";
      state.options.scales.x.title.font.family = "Open Sans";
      state.options.scales.y.title.font.family = "Open Sans";
      state.options.scales.x.title.font.size = 12;
      state.options.scales.y.title.font.size = 12;
      state.options.scales.x.title.color = "#000000";
      state.options.scales.y.title.color = "#000000";
      state.options.scales.x.border.color = "#000000";
      state.options.scales.y.border.color = "#000000";
      state.options.scales.x.border.display = true;
      state.options.scales.y.border.display = true;
      state.options.scales.x.grid.color = "#d3d3d3";
      state.options.scales.y.grid.color = "#d3d3d3";
      state.options.scales.x.ticks.font.family = "Open Sans";
      state.options.scales.y.ticks.font.family = "Open Sans";
      state.options.scales.x.ticks.font.size = 12;
      state.options.scales.y.ticks.font.size = 12;
      state.options.scales.x.ticks.color = "#000000";
      state.options.scales.y.ticks.color = "#000000";
      state.options.scales.x.ticks.display = true;
      state.options.scales.y.ticks.display = true;
    }

    state.options.plugins.datalabels.font.family = "Open Sans";
    state.options.plugins.datalabels.font.size = 12;
    state.options.plugins.datalabels.color = "#000000";
    state.options.plugins.datalabels.display = false;
    state.options.plugins.legend.labels.color = "#000000";
    state.options.plugins.legend.labels.font.size = 12;
    state.options.plugins.legend.labels.font.family = "Open Sans";
    state.options.plugins.legend.display = true;
    state.options.plugins.tooltip.backgroundColor = "#ffffff";
    state.options.plugins.tooltip.titleColor = "#000000";
    state.options.plugins.tooltip.bodyColor = "#000000";

    if (isVerticalBar) {
      state.options.scales.x.grid.display = false;
      state.options.scales.y.grid.display = true;
      state.options.plugins.legend.labels.pointStyle = "rect";
      state.options.scales.x.offset = true;
      state.options.plugins.datalabels.align = "end";
      state.options.plugins.datalabels.anchor = "end";

      let dataSet = [];
      let newData = {};
      state.data.datasets.forEach(data => {
        newData = {
          ...data,
          backgroundColor: data.borderColor,
        };

        dataSet.push(newData);
      });
      state.data.datasets = dataSet;
    } else if (isStackedVerticalBar) {
      state.options.scales.x.grid.display = false;
      state.options.scales.y.grid.display = true;
      state.options.plugins.legend.labels.pointStyle = "rect";
      state.options.scales.x.offset = true;
      state.options.plugins.datalabels.align = "center";
      state.options.plugins.datalabels.anchor = "center";

      let dataSet = [];
      let newData = {};
      state.data.datasets.forEach(data => {
        newData = {
          ...data,
          backgroundColor: data.borderColor,
        };

        dataSet.push(newData);
      });
      state.data.datasets = dataSet;
    } else if (isHorizontalBar) {
      state.options.scales.x.grid.display = true;
      state.options.scales.y.grid.display = false;
      state.options.plugins.legend.labels.pointStyle = "rect";
      state.options.scales.x.offset = false;
      state.options.plugins.datalabels.align = "end";
      state.options.plugins.datalabels.anchor = "end";

      let dataSet = [];
      let newData = {};
      state.data.datasets.forEach(data => {
        newData = {
          ...data,
          backgroundColor: data.borderColor,
        };

        dataSet.push(newData);
      });
      state.data.datasets = dataSet;
    } else if (isStackedHorizontalBar) {
      state.options.scales.x.grid.display = true;
      state.options.scales.y.grid.display = false;
      state.options.plugins.legend.labels.pointStyle = "rect";
      state.options.scales.x.offset = false;
      state.options.plugins.datalabels.align = "center";
      state.options.plugins.datalabels.anchor = "center";

      let dataSet = [];
      let newData = {};
      state.data.datasets.forEach(data => {
        newData = {
          ...data,
          backgroundColor: data.borderColor,
        };

        dataSet.push(newData);
      });
      state.data.datasets = dataSet;
    } else if (isLine) {
      state.options.scales.x.grid.display = true;
      state.options.scales.y.grid.display = true;
      state.options.elements.point.radius = 5;
      state.options.elements.point.hoverRadius = 5;
      state.options.plugins.legend.labels.pointStyle = "circle";
      state.options.scales.x.offset = true;
      state.options.plugins.datalabels.align = "end";
      state.options.plugins.datalabels.anchor = "end";

      let dataSet = [];
      let newData;
      state.data.datasets.forEach(data => {
        newData = {
          ...data,
          backgroundColor: data.borderColor,
          fill: false,
        };

        dataSet.push(newData);
      });
      state.data.datasets = dataSet;
    } else if (isArea) {
      state.options.scales.x.grid.display = true;
      state.options.scales.y.grid.display = true;
      state.options.elements.point.radius = 5;
      state.options.elements.point.hoverRadius = 5;
      state.options.plugins.legend.labels.pointStyle = "circle";
      state.options.scales.x.offset = true;
      state.options.plugins.datalabels.align = "end";
      state.options.plugins.datalabels.anchor = "end";

      let dataSet = [];
      let newData;
      state.data.datasets.forEach(data => {
        newData = {
          ...data,
          backgroundColor: data.borderColor + "4D",
        };

        dataSet.push(newData);
      });
      state.data.datasets = dataSet;
    } else if (isPieOrDonut) {
      state.options.plugins.legend.labels.pointStyle = "rect";
      state.options.plugins.datalabels.align = "start";
      state.options.plugins.datalabels.anchor = "end";

      let newPieExtra = [];
      pieDonutExtraData.current.forEach((pieDonut, idx) => {
        let tempData = { ...pieDonut, backgroundColor: state.pieData.datasets[0].backgroundColor };
        newPieExtra.push(tempData);
      });

      pieDonutExtraData.current = newPieExtra;
    }
    setEditModeActiveTab(0);
    setIsTabSelected(false);
    setChartDropDownChanged(prev => !prev);
    setChartOptionData(state);
  }, [chartDropDownValue]);

  function deepEqual(obj1, obj2, path = "") {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) {
      return false;
    }

    if (typeof obj1 !== "object" || typeof obj2 !== "object") {
      return false;
    }

    if (Array.isArray(obj1) !== Array.isArray(obj2)) {
      return false;
    }

    if (Array.isArray(obj1)) {
      if (obj1.length !== obj2.length) {
        return false;
      }
      for (let i = 0; i < obj1.length; i++) {
        if (!deepEqual(obj1[i], obj2[i], `${path}[${i}]`)) return false;
      }
      return true;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    const allKeys = new Set([...keys1, ...keys2]);

    for (const key of allKeys) {
      if (!keys1.includes(key)) {
        return false;
      }
      if (!keys2.includes(key)) {
        return false;
      }
      if (!deepEqual(obj1[key], obj2[key], path ? `${path}.${key}` : key)) return false;
    }
    return true;
  }

  useEffect(() => {
    if (["pie", "donut"].includes(chartDropDownValue) && chartOptionData.pieData.labels.length === 0)
      setDisableInsertUpdate(true);
    else if (!["pie", "donut"].includes(chartDropDownValue) && chartOptionData.data.datasets.length === 0)
      setDisableInsertUpdate(true);
    else setDisableInsertUpdate(false);
    
    if (clickedUndoStack.current) {
      if (clickedUndoStack.current) clickedUndoStack.current = false;
      return;
    }

    if (!initComplete && editable) return;

    if (
      !deepEqual(lastSavedChartOption.current, {
        chartTypeDropDown: chartDropDownValue,
        chartOptionData: chartOptionData,
        pieExtraData: pieDonutExtraData.current,
      })
    ) {
      if (undoStack.current.length === 0 && lastSavedChartOption.current == null) {
        initialChartState.current = {
          chartTypeDropDown: chartDropDownValue,
          chartOptionData: chartOptionData,
          pieExtraData: pieDonutExtraData.current,
        };
      } else {
        undoStack.current.push(
          customClone({
            chartTypeDropDown: chartDropDownValue,
            chartOptionData: chartOptionData,
            pieExtraData: pieDonutExtraData.current,
          })
        );
      }

      lastSavedChartOption.current = customClone({
        chartTypeDropDown: chartDropDownValue,
        chartOptionData: chartOptionData,
        pieExtraData: pieDonutExtraData.current,
      });
      setUndoStateLength(undoStack.current.length);

      if (redoStack.current.length > 0) {
        redoStack.current = [];
        setRedoStateLength(0);
      }

      if (undoStack.current.length > MAX_STACK_SIZE) {
        undoStack.current.shift();
      }
    }
  }, [chartOptionData]);

  useEffect(() => {
    if (editable) {
      const targetWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);
      const config = widgets[targetWidgetIndex].chartConfig;
      const chartType = config.chartType;
      let labelFormat = config.labelFormat;
      const pieDonutConfig = config.pieDonutConfig;

      const baseOptions = config.chartBasedConfig.options || {};
      const basePlugins = baseOptions.plugins || {};

      if (labelFormat?.name !== "Percentage") {
        labelFormat = { name: "Value" };
      }

      let newTemp = {};

      if (["pie", "donut"].includes(chartType)) {
        let datalabels = [];
        let dataSets = [];

        config.chartBasedConfig.data.labels.forEach((label, idx) => {
          let newDataSet = {
            label: label,
            data: [],
            backgroundColor: "",
            borderColor: "",
            borderWidth: 1,
            fill: false,
          };
          dataSets.push(newDataSet);
        });

        config.chartBasedConfig.data.datasets.forEach((dataset, idx) => {
          datalabels.push(dataset.label);

          dataset.data.forEach((dataValue, index) => {
            if(dataValue == null) return;
            dataSets[index].data?.push(dataValue);
            dataSets[index].backgroundColor = dataset.backgroundColor[index];
            dataSets[index].borderColor = dataset.backgroundColor[index];
          });
        });
        pieDonutConfig.forEach((pieDonut, idx) => {
          datalabels.push(pieDonut.label);

          pieDonut.data.forEach((dataValue, index) => {
            if(dataValue == null) return;
            dataSets[index].data?.push(dataValue);
            dataSets[index].backgroundColor = pieDonut.backgroundColor[index];
            dataSets[index].borderColor = pieDonut.backgroundColor[index];
          });
        });

        newTemp = {
          labels: datalabels,
          datasets: dataSets,
        };
      } else {
        let pieDataLabels = [];
        let pieDataDataSetsData = [];
        let pieDataDataSetsLabel = "";
        let pieDataDataSetBackgroundColor = [];

        pieDataDataSetsLabel = config.chartBasedConfig.data.labels[0];

        config.chartBasedConfig.data.datasets.forEach((pieData, idx) => {
          pieDataLabels.push(pieData.label);
          pieDataDataSetsData.push(pieData.data[0]);
          pieDataDataSetBackgroundColor.push(pieData.backgroundColor);
        });

        newTemp = {
          labels: pieDataLabels,
          datasets: [
            {
              label: pieDataDataSetsLabel,
              data: pieDataDataSetsData,
              backgroundColor: pieDataDataSetBackgroundColor,
              borderColor: "",
              borderWidth: 1,
            },
          ],
        };
      }

      const chartConfig = {
        data: ["pie", "donut"].includes(chartType) ? newTemp : config.chartBasedConfig.data,
        options: {
          ...baseOptions,
          plugins: {
            ...basePlugins,
            tooltip: {
              ...basePlugins.tooltip,
              callbacks: {
                ...basePlugins.tooltip?.callbacks,
                label: function (context) {
                  let value;
                  const chartType = context.chart.config.type;

                  if (chartType === "bar" && context.chart.config.options.indexAxis === "y") {
                    value = context.parsed.x;
                  } else if (chartType === "bar" || chartType === "line") {
                    value = context.parsed.y;
                  } else {
                    value = context.raw;
                  }
                  const label = context.dataset.label || "";
                  return `${label}: ${value}`;
                },
              },
            },
            datalabels: {
              ...basePlugins.datalabels,
              formatter: (value, context) => {
                if (["pie", "donut"].includes(chartType) && labelFormat?.name === "Percentage") {
                  const data = context.dataset.data;
                  const validData = data.filter(num => typeof num === "number" && !isNaN(num));
                  const total = validData.reduce((acc, num) => acc + num, 0);
                  if (!total || typeof value !== "number" || isNaN(value)) return "";
                  return `${((value / total) * 100).toFixed(2)}%`;
                }
                return value;
              },
            },
            legend: {
              ...basePlugins.legend,
              onClick: (e, legendItem, legend) => {},
            },
          },
        },
        pieData: ["pie", "donut"].includes(chartType) ? config.chartBasedConfig.data : newTemp,
      };

      lastSavedChartOption.current = customClone({
        chartTypeDropDown: chartType,
        chartOptionData: chartConfig,
        pieExtraData: pieDonutConfig,
      });

      initialChartState.current = {
        chartTypeDropDown: chartType,
        chartOptionData: chartConfig,
        pieExtraData: pieDonutConfig,
      };

      // if(!["pie", "donut"].includes(chartType)){
      //   pieDonutExtraData.current = pieDonutInitialConfig;

      // }else{
      pieDonutExtraData.current = pieDonutConfig;

      // }

      setChartOptionData(chartConfig);
      setChartDropDownValue(chartType);
      setLabelFormat(labelFormat);
      const timeoutId = setTimeout(() => setInitComplete(true), 0);
      return () => clearTimeout(timeoutId);
    } else {
      setEditDataModeOn(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (closeChartTimeoutRef.current) clearTimeout(closeChartTimeoutRef.current);
      setInsertUpdateDisableButton(false);
    };
  }, []);

  return (
    <div className={cx(style["chart-modal-wrap"], style["table-modal-wrap"])} id="chart-modal">
      <div className={cx(style["d-flex"], style["justify-content-between"], style["modal-header"])}>
        {!editDataModeOn && <h4 className={cx(style["fw-7"], style["mb-0"])}>Charts</h4>}
        {editDataModeOn && (
          <div className={cx(style["d-flex"], style["align-items-center"])}>
            <Icon icon="ui-arrow-left" additionalclass="me-2 cursor-pointer" onClick={() => setEditDataModeOn(false)} />
            <h4 className={cx(style["fw-7"], style["mb-0"])}>{mapName}</h4>
          </div>
        )}

        <div>
          <ul className={cx(style["undo-redo-area"])}>
            <li className={cx(style["custom-tooltip"], { [style["disabled"]]: undoStateLength === 0 })}>
              <span className={cx(style["cursor-pointer"], style["d-flex"])} onClick={() => handleUndoTask()}>
                <Icon icon="ui-undo" />
              </span>
              <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Undo</div>
            </li>
            <li className={cx(style["custom-tooltip"], { [style["disabled"]]: redoStateLength == 0 })}>
              <span className={cx(style["cursor-pointer"], style["d-flex"])} onClick={() => handleRedoTask()}>
                <Icon icon="ui-redo" />
              </span>
              <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Redo</div>
            </li>
          </ul>
          <span
            className={cx(style["expand-btn"], style["rounded"])}
            onClick={() => setModalFullScreen(!modalFullScreen)}>
            <Icon icon={modalFullScreen ? "ui-collapse" : "ui-expand"} />
          </span>
          <span
            className={cx(style["cross-modal"], style["rounded"])}
            onClick={() => {
              closeModal(), setModalFullScreen(false);
            }}>
            <Icon icon="ui-close" />
          </span>
        </div>
      </div>

      <Row className={cx(style["table-modal-tab"], style["chart-modal-tab"], style["m-0"])}>
        <Col xs={4} className={cx(style["TMleft-pane"], style["pt-3"])}>
          <UncontrolledDropdown className={cx(style["w-100"], style["status"], style["mb-3"])}>
            <DropdownToggle caret tag="a" className={style["rounded"]}>
              <span className={cx(style["d-flex"], style["align-items-center"])}>
                <Icon icon={chartDropDown[chartDropDownValue]?.chart_icon} additionalclass="me-2"></Icon>{" "}
                <span>{chartDropDown[chartDropDownValue]?.chart_name}</span>
              </span>
              <Icon icon="ui-arrow-down" />
            </DropdownToggle>
            <DropdownMenu className={cx(style["shadow"], style["border-0"], style["rounded"], style["w-100"])}>
              {Object.keys(chartDropDown).map((keys, idx) => (
                <DropdownItem
                  tag={"a"}
                  key={idx}
                  className={cx({
                    [style["active-item"]]: keys == chartDropDownValue,
                  })}
                  onClick={() => keys !== chartDropDownValue && updateChartDropDown(keys)}>
                  <Icon icon={chartDropDown[keys].chart_icon} additionalclass="me-2"></Icon>{" "}
                  <span>{chartDropDown[keys].chart_name}</span>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
          <Nav tabs className={cx(style["mb-3"], style["border-bottom-tab"])}>
            <NavItem className={cx(style["m-0"], style["p-0"])}>
              <NavLink
                className={cx({ [style["active"]]: editModeActiveTab === 0 })}
                onClick={() => {
                  setEditModeActiveTab(0), setSettingsTabCurrentCaption("*");
                }}>
                Data
              </NavLink>
            </NavItem>
            <NavItem className={cx(style["m-0"], style["p-0"])}>
              <NavLink
                className={cx({ [style["active"]]: editModeActiveTab === 1 })}
                onClick={() => setEditModeActiveTab(1)}>
                Settings
              </NavLink>
            </NavItem>
          </Nav>

          <div className={cx(style["tab-pane"], { [style["active"]]: editModeActiveTab === 0 })}>
            <SheetData
              setChartOptionData={setChartOptionData}
              chartOptionData={chartOptionData}
              chartDropDownValue={chartDropDownValue}
              chartDropDownChanged={chartDropDownChanged}
              clickedUndotask={clickedUndotask}
              pieDonutExtraData={pieDonutExtraData}
            />
          </div>

          <div className={cx(style["tab-pane"], { [style["active"]]: editModeActiveTab === 1 })}>
            {settingsTabCurrentCaption === "*" && (
              <div>
                {!isTabSelected && (
                  <Nav tag="div" tabs vertical>
                    {chartDropDown[chartDropDownValue]?.settings?.map((data, index) => (
                      <NavItem key={index}>
                        <NavLink tag="span" onClick={() => toggleTab(data?.name)}>
                          <Icon icon={data.icon}></Icon> {data?.name}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                )}

                {isTabSelected && (
                  <>
                    <h6
                      onClick={toggleTab}
                      className={cx(
                        style["mb-3"],
                        style["pt-2"],
                        style["fw-6"],
                        style["d-flex"],
                        style["align-items-center"],
                        style["cursor-pointer"],
                        style["ms-n1"]
                      )}>
                      <Icon icon="ui-arrow-left" additionalclass="me-2" />
                      {activeSettingTab}
                    </h6>
                    <div className={cx(style["table-option"], style["ps-3"], style["pe-0"], style["ms-2"])}>
                      <div className={style["table-option-inside"]}>
                        {activeSettingTab === "Axes" && (
                          <Axes
                            chartDefaultData={chartDefaultData}
                            chartOptionData={chartOptionData}
                            setChartOptionData={setChartOptionData}
                            clickedUndotask={clickedUndotask}
                          />
                        )}
                        {activeSettingTab === "Grid" && (
                          <Grid
                            chartDefaultData={chartDefaultData}
                            chartOptionData={chartOptionData}
                            setChartOptionData={setChartOptionData}
                            chartType={chartDropDownValue}
                            clickedUndotask={clickedUndotask}
                          />
                        )}
                        {activeSettingTab === "Labels" && (
                          <Labels
                            chartDefaultData={chartDefaultData}
                            chartOptionData={chartOptionData}
                            setChartOptionData={setChartOptionData}
                            chartType={chartDropDownValue}
                            labelFormat={labelFormat}
                            setLabelFormat={setLabelFormat}
                            clickedUndotask={clickedUndotask}
                          />
                        )}
                        {activeSettingTab === "Legend" && (
                          <Legend
                            chartDefaultData={chartDefaultData}
                            chartOptionData={chartOptionData}
                            setChartOptionData={setChartOptionData}
                            clickedUndotask={clickedUndotask}
                          />
                        )}
                        {activeSettingTab === "Style" && (
                          <Style
                            chartDefaultData={chartDefaultData}
                            chartOptionData={chartOptionData}
                            setChartOptionData={setChartOptionData}
                            chartDropDownValue={chartDropDownValue}
                            clickedUndotask={clickedUndotask}
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Col>

        <Col xs={8} className={cx(style["TMright-pane"], style["pt-3"])}>
          <div className={cx(style["position-relative"], style["text-center"], style["py-2"], style["mb-2"])}></div>

          <div className={style["total-chart-wrap"]}>
            <ChartContent
              labelFormat={labelFormat}
              chartConfig={chartOptionData}
              chartType={chartDropDownValue}
              chartDropDownChange={chartDropDownChange.current}
            />
          </div>

          <div className={style["Mfooter"]}>
            <div className={cx(style["align-items-center"], style["d-flex"])}>
              {editable && (
                <Button cssModule={style} color="primary" onClick={updateChart} disabled={insertUpdateDisableButton || disableInsertUpdate}>
                  Update
                </Button>
              )}
              {!editable && (
                <Button cssModule={style} color="primary" onClick={insertChart} disabled={insertUpdateDisableButton || disableInsertUpdate}>
                  Insert
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

Chart.propTypes = {
  closeModal: PropTypes.func,
  modalFullScreen: PropTypes.bool,
  setModalFullScreen: PropTypes.func,
};

export default Chart;
